import React, { useCallback, useEffect, useMemo, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getSessionUserId } from '@/utils/sessionUserId';

import { 
  CHATBOTS_WITH_NEW_DESIGN, 
  DANTE_ICON, 
  DEFAULT_CHAT_MESSAGES, 
  LLM_MODEL_DEFAULT, 
  STATUS,
  RESOLUTION_FEEDBACK_QUESTIONS,
  RESOLUTION_RESPONSE_MESSAGES,
  RESOLUTION_NEGATIVE_MESSAGES,
  RESOLUTION_THANK_YOU_MESSAGES,
  ACKNOWLEDGMENT_MESSAGES
} from '@/constants';
import formatEventData from '@/helpers/formatEventData';
import transformMessageToBubbleMessage from '@/helpers/transformMessageToBubbleMessage';
import calculateReadingDelay from '@/helpers/calculateReadingDelay';
import useTaskHandler from '@/hooks/useTaskHandler';
import usePromptSuggestions from '@/hooks/usePromptSuggestions';
import {
  sendMessageEmbed,
  sendMessageInApp,
  createSharedFormData,
  requestTakeover,
  pollTakeover,
  showDynamicButtons,
  getResolution,
} from '@/services/chat.service';
import * as conversationsService from '@/services/conversations.service';
import * as customizationService from '@/services/customization.service';
import * as humanHandoverService from '@/services/humanHandover';
import * as messagesService from '@/services/message.service';
import * as suggestionService from '@/services/suggestion.service';
import * as userService from '@/services/user.service';
import { useConversationStore } from '@/hooks/useConversationStore';
import useTaskStore from '@/stores/task/taskStore';

import V3Chat from '@/v3/components/Chat';
import ResolutionFeedbackPopup from '../ResolutionFeedbackPopup';
import { useChatbotStore } from '@/stores/chatbot/chatbotStore';
import { useSearchParams } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { DateTime } from 'luxon';
import * as ttsService from '@/services/tts.service';
import { usePreviousConversationsStore } from '@/stores/previousConversation/previousConversationStore';
import PropTypes from 'prop-types';
import { useUserStore } from '@/stores/user/userStore';
import * as modelService from '@/services/model.service';
import transformMessageToBubbleMessageHumanHandover from '@/helpers/transformMessageToBubbleMessageHumanHandover';
import { useHumanHandoverStore } from '@/stores/humanHandover/humanHandoverStore';
import { showNotificationWithSound, unlockAudioContext } from '@/helpers/notificationHelper';
import DSpinner from '@/components/Global/DSpinner';
import useHumanHandoverWebSocket from '@/hooks/useHumanHandoverWebSocket';
import VoicePreview from '@/components/Voice/VoicePreview';
import { WS_ENDPOINT_TYPES } from '@/services/websocket/voiceWebSocketUtils';
import { useTimezoneStore } from '@/stores/timezone/timezoneStore';
import { useSoundEffects } from '@/hooks/useSoundEffects';
import { IOS_ICONS_MAP, IOS_ICON_OPTIONS } from '@/components/Chatbot/Setup/iosIconsMap';
import featureCheck from '@/helpers/tier/featureCheck';
import { getEmojiIconComponent } from '@/v3/helpers/iconMapping';
import {
  getStoredLeadGenStatus,
  setStoredLeadGenStatus,
  clearStoredLeadGenStatus
} from '@/utils/leadGenStorage';


class RetriableError extends Error {}
class FatalError extends Error {}

// Custom hook to get the conversation store with the correct chatbot ID and interface type

const ChatManager =  forwardRef(({
  config,
  hiddenConversation = false,
  isInApp = false,
  isDanteFaq = false,
  showMenuBtn = true,
  showCloseBtn = false,
  handleOpenDanteConversations,
  handleOpenDanteFaq,
  handleCloseButton,
  showInAppHeader = true,
  hiddenLlmSelector = false,
  initialShouldFetchCustomization = true,
  isPreviewMode = false,
  cookie,
  setCookie,
  forceRefetch,
  promptToSend,
  setPromptToSend,
  onPromptProcessed,
  isInHumanHandoverApp = false,
  isLiveAgentConversationResolved = false,
  liveAgentConversation = null,
  webSocketHook = null,
  showEmailTranscript = false,
  setShowEmailTranscript = () => {},
  onNewConversationCreated,
  setIsHumanHandover,
  setAgentName,
  setAgentTitle,
  setAgentAvatar,
  setAgentJoined,
  isDirectLink = false,
}, ref) => {
  const [urlParams] = useSearchParams();
  
  // Determine the interface type based on props
  const interfaceType = isInApp ? 'in-app' : isInHumanHandoverApp ? 'human-handover' : isDirectLink ? 'direct-link' : 'bubble';
  
  // Use the conversation store with automatic namespacing
  const {
    currentConversation,
    setCurrentConversation,
    resetCurrentConversation
  } = useConversationStore(config?.kb_id, interfaceType);
  
  // Add a reference to track the previous chatbot ID
  const prevChatbotIdRef = useRef(config?.kb_id);
  // Add a reference to track the previous conversation ID
  const prevConversationIdRef = useRef(currentConversation?.id);
  
  // Handle cleanup when switching between chatbots
  useEffect(() => {
    const currentChatbotId = config?.kb_id;
    const prevChatbotId = prevChatbotIdRef.current;
    
    // If the chatbot ID has changed, reset the conversation
    if (prevChatbotId && currentChatbotId && prevChatbotId !== currentChatbotId) {
      resetCurrentConversation();
    }
    
    // Update the previous chatbot ID reference
    prevChatbotIdRef.current = currentChatbotId;
    
    // Cleanup function to handle unmounting or chatbot switching
    return () => {
      // If we're unmounting with a specific chatbot ID, make sure to clean up
      if (currentChatbotId) {
        // No need to reset here as we'll do it on mount if the ID changes
      }
    };
  }, [config?.kb_id, resetCurrentConversation, interfaceType, currentConversation]);

  const { addOpenedConversation, setLiveAgentConversationEvent, getLiveAgentConversationEvents, deleteLiveAgentConversationEvent, setFileUploadSetting, fileUploadSetting, setExplicitlyEnabledFileUpload, explicitlyEnabledFileUpload } = useHumanHandoverStore((state) => state);



  const selectedChatbot = useChatbotStore((state) => state.selectedChatbot);
  const setUserData = useUserStore((state) => state.setUser);
  const userData = useUserStore((state) => state.user);
  const auth = useUserStore((state) => state.auth);
  const setSaveSignUpBtn = useChatbotStore((state) => state.setSaveSignUpBtn);

  const [customizationData, setCustomizationData] = useState(config);

  // Access parentUrl from URL search parameters
  const parentUrl = urlParams.get('parentUrl') || '';

  const [messages, setMessages] = useState([]);
  

  const [control, setControl] = useState(null);
  const [shouldFetchCustomization, setShouldFetchCustomization] = useState(
    initialShouldFetchCustomization
  );
  const [isAnswerLoading, setIsAnswerLoading] = useState(false);
  //show buttons in chat (suggested prompts, live agent, etc)
  const [showButtons, setShowButtons] = useState(true);
  const [interactingWithLiveAgent, setInteractingWithLiveAgent] =
    useState(false);
  const [userName, setUserName] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [hasAskedForName, setHasAskedForName] = useState(false);
  const [pollResponse, setPollResponse] = useState(false);
  const [isLiveAgentResolved, setIsLiveAgentResolved] = useState(isLiveAgentConversationResolved);
  const [agentImage, setAgentImage] = useState(null);
  const [sendEventConnected, setSendEventConnected] = useState(null);
  const queryStreamOpen = useRef(false);
  const wsRef = useRef(null);
  const activeConnectionIdRef = useRef(null);
  const liveAgentData = useHumanHandoverStore((state) => state.userOrganization);

  const timezone = useTimezoneStore((state) => state.timezone);
  
  // Initialize sound effects hook with customization data
  const { playLiveAgentMessageSound } = useSoundEffects({ customizationData });

  /**
   * Handles adding or updating messages in the chat.
   *
   * @param {string} action - The action to perform ('add' or 'update').
   * @param {Object} message - The message object to add or update.
   */
  const handleMessagesOnChat = useCallback(
    (action, message) => {
      
      if (action === 'add') {
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages, message];
          return updatedMessages;
        });
      }
      if (action === 'update') {
        setMessages((prevMessages) => {
          let hasChanges = false;
          const updatedMessages = prevMessages.map((msg) => {
            if (msg.id && msg.id === message.id) {
              // Check if the message actually changed
              const hasActualChanges = Object.keys(message).some(key => {
                // if (key === 'id' || key === 'id_temp') return false; // Skip ID fields
                return msg[key] !== message[key];
              });

              if (hasActualChanges) {
                hasChanges = true;
                return { ...msg, ...message };
              } else {
                return msg;
              }
            }
            if (msg.id_temp && msg.id_temp === message.id_temp) {
              // Check if the message actually changed
              const hasActualChanges = Object.keys(message).some(key => {
                // if (key === 'id' || key === 'id_temp') return false; // Skip ID fields
                return msg[key] !== message[key];
              });

              if (hasActualChanges) {
                hasChanges = true;
                return { ...msg, ...message };
              } else {
                return msg;
              }
            }
            return msg;
          });

          if (hasChanges) {
            return updatedMessages;
          } else {
            return prevMessages; // Return the same reference to prevent re-render
          }
        });
      }

    },
    []
  );

  // Expose handleMessagesOnChat function through ref
  useImperativeHandle(ref, () => ({
    handleMessagesOnChat
  }), [handleMessagesOnChat]);

  // Always initialize the WebSocket hook to maintain consistent hook order
  const internalWebSocketHook = useHumanHandoverWebSocket({
    token: config?.token,
    isInApp,
    isInHumanHandoverApp,
    wsRef,
        onMessageReceived: (parsedData) => {
      
      // Handle incoming message
      const newMessage = {
        role: isInHumanHandoverApp ? 'user' : 'assistant',
        content: parsedData.data.text,
        type: CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id) ? 'human_handover_message' : 'message',
        agent_name: parsedData.data.agent_name || liveAgentData?.name,
        agent_profile_pic: parsedData.data.agent_profile_pic || liveAgentData?.image,
        agent_role: parsedData.data.agent_role || liveAgentData?.agent_title,
        agent_title: parsedData.data.agent_title || liveAgentData?.agent_title,
        images: parsedData.data.images,
        date_created: parsedData?.data?.date_created || new Date()
      };


      handleMessagesOnChat('add', newMessage);

      if (isInHumanHandoverApp) {
        showNotificationWithSound('New Message', {
          body: 'User said: ' + parsedData.data.text,
          icon: DANTE_ICON,
        });
      } else {
        playLiveAgentMessageSound();
      }
    },
    onAgentJoined: ({ infoAgent }) => {
      setAgentName && setAgentName(infoAgent?.name || infoAgent?.agent_name);
      setAgentTitle && setAgentTitle(infoAgent?.agent_title);
      setAgentAvatar && setAgentAvatar(infoAgent?.image || infoAgent?.agent_profile_pic);
      setAgentJoined && setAgentJoined(true); // Set agent joined flag
      setIsHumanHandover && setIsHumanHandover(true);
      
      // Find and update the connecting message with fade out effect
      setMessages(prevMessages => {
        const updatedMessages = prevMessages.map(msg => {
          if (msg.isConnecting) {
            return {
              ...msg,
              isFadingOut: true // Flag to trigger fade out animation
            };
          }
          return msg;
        });
        return updatedMessages;
      });

      // After a short delay, replace the connecting message with the joined message
      setTimeout(() => {
        setMessages(prevMessages => {
          const messagesWithoutConnecting = prevMessages.filter(msg => !msg.isConnecting);
          
          // Add the agent joined message with fade in effect
          const agentJoinedMessage = {
            id: uuidv4(),
            type: 'info',
            content: `Team member ${infoAgent?.agent_name ? infoAgent?.agent_name : ''} has joined the chat`,
            isFadingIn: true // Flag to trigger fade in animation
          };
          
          return [...messagesWithoutConnecting, agentJoinedMessage];
        });

        // Remove the fade in flag after animation completes
        setTimeout(() => {
          setMessages(prevMessages => {
            return prevMessages.map(msg => {
              if (msg.isFadingIn) {
                const { isFadingIn, ...messageWithoutFlag } = msg;
                return messageWithoutFlag;
              }
              return msg;
            });
          });
        }, 300); // Match CSS animation duration
      }, 300); // Wait for fade out to complete

      // File upload settings will be fetched when agent joins via humanHandoverTakeConversation
      // Do not fetch here to avoid premature API calls before the Join button is clicked

      // For user side (shared chat bubble) - using store-based updates, no API polling needed
      if (!isInApp && !isInHumanHandoverApp && config?.token) {
       
      }
    },
    onAgentResolved: ({ infoAgent }) => {
      // Agent has resolved the conversation
      const newMessage = {
        type: 'info',
        content: `Conversation has been marked as resolved by team member ${infoAgent?.agent_name ? infoAgent?.agent_name : ''}. Thank you for using our service`,
      };
      handleMessagesOnChat('add', newMessage);
      setPollResponse(false);
      setIsHumanHandover && setIsHumanHandover(false);
      setIsLiveAgentResolved(true);
    }
  });

  // Use the provided webSocketHook or the internal one
  const {
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
    sendMessage: sendWebSocketMessage,
    sendControlMessage: sendWebSocketControlMessage,
    sendTypingStart: sendWebSocketTypingStart,
    sendTypingStop: sendWebSocketTypingStop,
    isConnected: isWebSocketConnected,
    isTyping: isWebSocketTyping,
    typingUser: webSocketTypingUser,
  } = webSocketHook || internalWebSocketHook;
  const [notification, setNotification] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [questionIsQuickResponse, setQuestionIsQuickResponse] = useState(false);
  const [sourcesReady, setSourcesReady] = useState(false);
  const [sourcesLoading, setSourcesLoading] = useState([]);
  const [onGoingStream, setOnGoingStream] = useState(false);
  const [messageLoaded, setMessageLoaded] = useState(false);
  const [speechPlaying, setSpeechPlaying] = useState(false);
  const [speechPlayingID, setSpeechPlayingID] = useState('');
  const [played, setPlayed] = useState([]);
  const [hideFooter, setHideFooter] = useState(false);
  const synth = window.speechSynthesis;
  const voices = synth?.getVoices();

  const [userIsTyping, setUserIsTyping] = useState(false);
  const resolutionTimeoutsRef = useRef({});
  const stoppedTypingTimeoutRef = useRef(null);
  // Store which messages have is_resolved === true and should show popup
  const resolvedMessagesRef = useRef({});
  // Track if the user's next message should be treated as resolution feedback
  const isResolvingMessageRef = useRef(null);
  // Track if we're currently in resolution mode (showing resolution feedback)
  const [isInResolutionMode, setIsInResolutionMode] = useState(false);
  // Track if we're currently showing CSAT feedback
  const [isShowingCsat, setIsShowingCsat] = useState(false);
  // Track the CSAT option number selected by the user
  const csatScoreRef = useRef(null);
  // Track if we're sending CSAT feedback (to abort stream early)
  const isSendingCsatRef = useRef(false);
  // Track if this is a resolution follow-up ("I have another question" scenario)
  const isResolutionFollowUpRef = useRef(false);
  // Track if we should send returned_to_conversation on the next message (when returning to conversation with both CSAT and resolution incomplete)
  const shouldSendReturnedToConversationRef = useRef(false);

  const isAgentAvailable = useMemo(() => {
    if (config?.live_agent_always_active) {
      return true;
    }

    if (!config?.talk_to_live_agent || !config?.live_agent_list_of_schedules?.length) {
      return false;
    }

    try {
      // Get the configured timezone or default to UTC
      const timezone = config?.human_handover_timezone || Intl?.DateTimeFormat()?.resolvedOptions()?.timeZone || 'Europe/Belgrade';
      
      // Get current date/time in the configured timezone using Luxon
      const now = DateTime.now().setZone(timezone);
      const currentDay = now.weekdayLong;
      const currentTime = now.toFormat('HH:mm');

      const schedule = config.live_agent_list_of_schedules.find(s => s.day === currentDay);
      if (!schedule || !schedule.from || !schedule.to) {
        return false;
      }

      // Convert schedule times to DateTime objects in the configured timezone for comparison
      const scheduleStart = DateTime.fromFormat(schedule.from, 'HH:mm', { zone: timezone });
      const scheduleEnd = DateTime.fromFormat(schedule.to, 'HH:mm', { zone: timezone });
      const currentDateTime = DateTime.fromFormat(currentTime, 'HH:mm', { zone: timezone });

      return currentDateTime >= scheduleStart && currentDateTime <= scheduleEnd;
    } catch (error) {
      console.error('Error checking live agent availability:', error);
      return false;
    }
  }, [config?.talk_to_live_agent, config?.live_agent_list_of_schedules, config?.human_handover_timezone, config?.live_agent_always_active]);

  const [dynamicButtons, setDynamicButtons] = useState({
    show_calendly_url: (config?.show_calendly_at_the_start && config?.calendly_integration_enabled) || false,
    show_data_form: config?.show_data_collection_on_conversation_begin || false,
    show_live_agent: config?.talk_to_live_agent && (
      config?.show_live_agent_on_conversation_begin || isAgentAvailable
    ) || false,
  });



  // Initialize lead generation state with stored values (dynamic based on config)
  const initialLeadGenStatus = getStoredLeadGenStatus(config?.kb_id, config);

  const [isLeadGenMode, setIsLeadGenMode] = useState(
    config?.data_collection && !initialLeadGenStatus.completed && !initialLeadGenStatus.declined
      ? config?.show_data_collection_on_conversation_begin
      : false
  );
  const [leadGenDeclined, setLeadGenDeclined] = useState(initialLeadGenStatus.declined);
  const [leadGenCompleted, setLeadGenCompleted] = useState(initialLeadGenStatus.completed);
  const leadGenCompletedRef = useRef(initialLeadGenStatus.completed); // Synchronous flag for immediate checks
  const [leadGenInputs, setLeadGenInputs] = useState({
    input_id: null,
    question: null,
    input_type: null,
    input_options: [],
    input_label: null,
    show_options: true,
  });

  const [savedQuestion, setSavedQuestion] = useState(null);
  const [firstQuestion, setFirstQuestion] = useState(true);
  const [showConsent, setShowConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { handleTask } = useTaskHandler();
  const [chatImageLoader, setChatImageLoader] = useState(false);
  const [hasMic, setHasMic] = useState(config?.has_mic);

  const addConversation = usePreviousConversationsStore(
    (state) => state.addConversation
  );

  const [sources, setSources] = useState([]);
  const [openSources, setOpenSources] = useState(false);
  const [showSources, setShowSources] = useState([]);

  const hasLoadedMessages = useRef(false);

  const [messagesLoading, setMessagesLoading] = useState(false);
  const [showVoicePreview, setShowVoicePreview] = useState(false);
  const [isPreviousConversationHasLiveAgent, setIsPreviousConversationHasLiveAgent] = useState(false);
  const [hasStartedConversation, setHasStartedConversation] = useState(false);


  // Use the prompt suggestions hook
  const {
    suggestions: suggestionPrompts,
    shouldShow: showSuggestionPrompts,
    isLoadingSuggestions,
    markAsShown,
    resetPrompts,
    onResponseComplete
  } = usePromptSuggestions({
    config: customizationData,
    conversationId: currentConversation?.id,
    isAnswerLoading,
    isLeadGenMode,
    interactingWithLiveAgent,
    isLiveAgentResolved,
    pollResponse,
    hasStartedConversation,
    showConsent,
    isInApp
  });

  /**
   * Creates a new conversation and returns its data.
   * @returns {Object|null} The conversation data or null if an error occurs.
   */
  const createConversation = useCallback(async () => {
    try {
      const res = await conversationsService.postCreateConversation({
        kb_id: config?.kb_id,
      });
      return res?.data || {};
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  }, [config?.id]);

  /**
   * Creates a new conversation for embeds (bubble, direct link, and embed) and returns its data.
   * @returns {Object|null} The conversation data or null if an error occurs.
   */
  const createSharedConversation = useCallback(async () => {
    try {
      const res = await conversationsService.postSharedCreateConversation({
        kb_id: config?.kb_id,
        token: config?.token,
        client_full_name: urlParams.get('client_full_name'),
        client_email: urlParams.get('client_email'),
        from_page: urlParams.get('from_page')
      });

      // Notify parent window about the new conversation (for iframe embeds)
      if (window.parent !== window && res.data?.id) {
        window.parent.postMessage({
          eventType: 'conversationCreated',
          conversationId: res.data.id,
          kbId: config?.kb_id
        }, '*');
      }

      return res.data;
    } catch (error) {
      console.error('Error creating conversation:', error);
      return null;
    }
  }, [config?.id, config?.kb_id]);

  const createNewConversation = useCallback(async () => {
    try {
      const newConversation = isInApp || isPreviewMode
        ? await createConversation()
        : await createSharedConversation();

      if (newConversation) {
        setCurrentConversation({ ...newConversation, type: 'new' });
        return newConversation;
      }
    } catch (error) {
      console.error('Error creating conversation:', error);
    }
  }, [createConversation, createSharedConversation, isInApp, setCurrentConversation, config?.kb_id, interfaceType]);

  /**
   * Finalizes the process of generating an answer and requests new suggestions.
   *
   * This function is called when the process of generating an answer is complete.
   * It closes the query stream and triggers a request for new suggestions based on the current messages.
   */
  const finishGeneratingAnswer = useCallback(
    (currectConversationId, isNewConversation = false) => {
      queryStreamOpen.current = false;
      onResponseComplete(currectConversationId);
      
      setIsAnswerLoading(false);

      if (isNewConversation && isInApp && onNewConversationCreated) {
        // Wait a bit for the backend to generate the title, then notify parent
        setTimeout(async () => {
          try {
            // Fetch the updated conversation with the generated title
            const response = await conversationsService.getConversationsByKbId(config?.kb_id);
            const updatedConversation = response?.data?.results?.find(conv => conv.id === currectConversationId);

            if (updatedConversation) {
              onNewConversationCreated(updatedConversation);
            } else {
              // Fallback: notify with basic conversation data
              onNewConversationCreated({ id: currectConversationId });
            }
          } catch (error) {
            console.error('Error fetching updated conversation:', error);
            // Fallback: notify with basic conversation data
            onNewConversationCreated({ id: currectConversationId });
          }
        }, 1500); // Give the backend time to generate the title
      }
    },
    [onResponseComplete, isInApp, onNewConversationCreated]
  );



  const handleDynamicLeadGen = async (userAnswer, conversationId) => {
    const payload = {
      question: leadGenInputs.question || null,
      answer: userAnswer || null,
      input_id: leadGenInputs.input_id,
      stop_after: config?.data_collection?.length,
      conversation_id: conversationId,
      kb_id: config?.kb_id,
    };

    const res = await conversationsService.getDynamicLeadGen(payload);

    if (res.status === 200) {
      if (res.data.finished) {
        setIsLeadGenMode(false);
        setLeadGenCompleted(true);
        leadGenCompletedRef.current = true;
        // Store completion status in localStorage (dynamic based on config)
        setStoredLeadGenStatus(config?.kb_id, { completed: true, declined: false }, config);
        setShowButtons(true);
        setIsAnswerLoading(true);
        return false;
      } else {
        // Find the corresponding field in data_collection_fields
        const currentField = config?.data_collection_fields?.find(
          field => field.id === res.data.next_question_input_id
        );

        handleMessagesOnChat('add', {
          id: uuidv4(),
          content: res.data.next_question,
          type: 'message',
          role: 'assistant',
        });
        
        return {
          ...res.data,
          currentField // Include the field information
        };
      }
    }
    return null;
  };



  const evaluateRules = async (message, conversationId, type) => {
    try {
      const response = await showDynamicButtons(
        message,
        conversationId,
        urlParams.get('token')
        // {
        //   show_live_agent: connectedToLiveAgent,
        // }
      );
      const data = response.data;

      if (type === 'data_collection') {
        setDynamicButtons((prev) => ({
          ...prev,
          show_data_form: config?.data_collection && data.show_data_form,
        }));
      }

      if (type === 'live_agent') {
        setDynamicButtons((prev) => ({
          ...prev,
          show_live_agent: (config?.talk_to_live_agent && data.show_live_agent),
        }));
      }

      if (type === 'calendly') {
        setDynamicButtons((prev) => ({
          ...prev,
          show_calendly_url:
            config?.calendly_integration_enabled && data.show_calendly_url,
        }));
      }

      if (data.show_data_form && !isLeadGenMode && !isInApp && !pollResponse && !leadGenDeclined && !leadGenCompleted && !leadGenCompletedRef.current) {
        setIsLeadGenMode(true);
        console.log('here')
        if (firstQuestion && config?.is_data_collection_optional) {
          setShowConsent(true);
          handleMessagesOnChat('add', {
            id: uuidv4(),
            content:
              config?.data_collection_consent_text ||
              DEFAULT_CHAT_MESSAGES.CONSENT_MESSAGE,
            type: 'message',
            role: 'assistant',
          });
          setSavedQuestion({
            id: uuidv4(),
            content: message,
            type: 'message',
            role: 'user',
          });
          setFirstQuestion(false);
          return true;
        }
        const res = await handleDynamicLeadGen(
          message,
          conversationId
        );
        if (res) {
          setLeadGenInputs({
            input_id: res.next_question_input_id,
            question: res.next_question,
            input_type: res.currentField?.input_type,
            input_options: res.currentField?.input_options,
            input_label: res.currentField?.input_label,
            show_options: true,
          });
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error evaluating rules:', error);
    }
  };

  const pollAgentTakeover = async (hh_conversation_id) => {
    
    if (!hh_conversation_id) {
      return;
    }

    // Skip if already connected to this conversation
      if (activeConnectionIdRef.current === hh_conversation_id && isWebSocketConnected) {
        return;
      }

    try {
      setPollResponse(true);

      // Reset file upload settings for new human handover conversation
      setFileUploadSetting(null);
      setExplicitlyEnabledFileUpload(false);

      setInteractingWithLiveAgent(false);
      setIsAnswerLoading(false);

      // Disconnect any existing connection first
      disconnectWebSocket();
      
      // Add a small delay to ensure disconnect completes before connecting
      await new Promise(resolve => setTimeout(resolve, 300));

      connectWebSocket(hh_conversation_id);
      
      // Update active connection ID
      activeConnectionIdRef.current = hh_conversation_id;
    } catch (e) {
      console.error('Error in pollAgentTakeover:', e);
    }
  };

  /**
   * Cancels all pending resolution popups and timeouts
   * Called when user starts typing or when navigating away
   */
  const cancelAllPendingResolutions = useCallback(() => {
    Object.values(resolutionTimeoutsRef.current).forEach((timeoutId) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
    
    // Also cancel the stopped typing timeout
    if (stoppedTypingTimeoutRef.current) {
      clearTimeout(stoppedTypingTimeoutRef.current);
      stoppedTypingTimeoutRef.current = null;
    }
    
    // Remove only unanswered resolution feedback messages from the messages array
    setMessages((prev) => prev.filter(msg => 
      !(msg.type === 'resolution_feedback' && !msg.answered)
    ));
    
    resolutionTimeoutsRef.current = {};
  }, []);

  /**
   * Shows the resolution feedback as a message for a specific message
   * @param {string} messageId - The actual message ID
   */
  const showResolutionPopup = useCallback((messageId) => {
    // Don't show resolution popup if resolution is disabled
    if (!customizationData?.resolution_enabled) {
      return;
    }
    
    // Check if resolution feedback message already exists for this message
    setMessages((prev) => {
      const alreadyExists = prev.some(msg => 
        msg.type === 'resolution_feedback' && msg.related_message_id === messageId
      );
      
      if (alreadyExists) {
        return prev;
      }
      
      // Add resolution feedback as a message
      return [
        ...prev,
        {
          id: `resolution_feedback_${messageId}`,
          type: 'resolution_feedback',
          role: 'assistant',
          content: RESOLUTION_FEEDBACK_QUESTIONS[Math.floor(Math.random() * RESOLUTION_FEEDBACK_QUESTIONS.length)],
          related_message_id: messageId,
          date_created: new Date(),
        }
      ];
    });
  }, [customizationData]);

  /**
   * Schedules a resolution check after appropriate reading time
   * @param {string} messageIdTemp - Temporary message ID
   * @param {string} messageContent - Content of the message
   */
  const scheduleResolutionCheck = useCallback(
    async (messageIdTemp, messageContent) => {
      if (isInApp || isInHumanHandoverApp) {
        return;
      }

      // Don't schedule resolution check if resolution is disabled
      if (!customizationData?.resolution_enabled) {
        return;
      }

      try {
        let actualMessageId = null;
        
        // Retry logic: Try up to 5 times with 300ms intervals (total 1.5s max wait)
        for (let attempt = 0; attempt < 5; attempt++) {
          await new Promise(resolve => setTimeout(resolve, 300));
          
          setMessages((currentMessages) => {
            const actualMessage = currentMessages.find(
              (msg) => msg.id_temp === messageIdTemp && msg.id
            );
            
            if (actualMessage?.id) {
              actualMessageId = actualMessage.id;
            }
            
            return currentMessages;
          });
          
          if (actualMessageId) {
            break;
          }
        }

        if (!actualMessageId) {
          return;
        }

        const response = await getResolution(
          config?.kb_id,
          actualMessageId,
          config?.token
        );


        if (response.data?.is_resolved !== true) {
          return;
        }

        // Store this message as resolved
        resolvedMessagesRef.current[actualMessageId] = {
          messageId: actualMessageId,
          messageContent,
          tempId: messageIdTemp,
          timestamp: Date.now()
        };
        const delay = calculateReadingDelay(messageContent);

        const timeoutId = setTimeout(() => {
          // Check if user is currently typing by using state setter callback to get fresh value
          let isCurrentlyTyping = false;
          setUserIsTyping((currentTypingState) => {
            isCurrentlyTyping = currentTypingState;
            return currentTypingState; // Don't modify state
          });
          
          if (isCurrentlyTyping) {
            return;
          }

          showResolutionPopup(actualMessageId);
          
          const newTimeouts = { ...resolutionTimeoutsRef.current };
          delete newTimeouts[messageIdTemp];
          resolutionTimeoutsRef.current = newTimeouts;
        }, delay);

        resolutionTimeoutsRef.current[messageIdTemp] = timeoutId;
        
      } catch (error) {
        console.error('❌ Failed to fetch resolution:', error);
      }
    },
    [isInApp, isInHumanHandoverApp, config, showResolutionPopup, customizationData]
  );

  const cancelPendingResolutionChecks = useCallback(() => {
    // Cancel all pending timeouts (these are scheduled to show but haven't shown yet)
    Object.values(resolutionTimeoutsRef.current).forEach((timeoutId) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    });
    resolutionTimeoutsRef.current = {};
  }, []);

  /**
   * Checks if there are any resolved messages that should show feedback
   * Called when user stops typing for 3 seconds
   */
  const checkAndShowResolvedPopup = useCallback(() => {
    // Get the most recent resolved message
    const resolvedMessageIds = Object.keys(resolvedMessagesRef.current);
    
    if (resolvedMessageIds.length === 0) {
      return;
    }

    // Get the most recent one
    const sortedMessages = resolvedMessageIds
      .map(id => resolvedMessagesRef.current[id])
      .sort((a, b) => b.timestamp - a.timestamp);
    
    const mostRecent = sortedMessages[0];
    
    if (mostRecent) {
      console.log('✨ User stopped typing, showing resolution feedback for:', mostRecent.messageId);
      showResolutionPopup(mostRecent.messageId);
    }
  }, [showResolutionPopup]);

  //TODO: Refactor this in the future
  const connectToLiveAgent = async (userName, userEmail, conversation) => {
    try {
      // ✅ VALIDATE conversation
      if (!conversation?.id) {
        console.error('❌ [connectToLiveAgent] Invalid conversation:', conversation);
        throw new Error('Conversation ID is required to connect to live agent');
      }

      const formInputs = [
        {
          input_name: 'name',
          input_type: 'text',
          input_label: 'name',
          input_placeholder: 'name',
          is_mandatory: true,
          input_value: userName,
        },
        {
          input_name: 'email',
          input_type: 'email',
          input_label: 'email',
          input_placeholder: 'email',
          is_mandatory: true,
          input_value: userEmail,
        },
      ];
      await createSharedFormData(
        conversation.id,
        urlParams.get('token'),
        formInputs
      );
      const connectingMessageId = uuidv4();
      const infoTypeMessage = {
        id: connectingMessageId,
        content: 'Connecting you with a team member',
        type: 'info',
        isConnecting: true, // Flag to identify this as the connecting message
      };
      handleMessagesOnChat('add', infoTypeMessage);
      const response = await requestTakeover(
        conversation.id,
        urlParams.get('token'),
        {
          user_email: userEmail,
          user_name: userName,
          parent_url: parentUrl
        },
      );

      await pollAgentTakeover(response.data.id);
    } catch (error) {
      console.error('Error connecting to live agent:', error);
    }
  };

  /**
   * Sends a question to the server and manages the chat state during the process.
   * @param {Object} question - The question object containing content and metadata.
   * @param {Array} imageUrls - Optional array of image URLs to include with the message.
   */
  const sendQuestion = useCallback(
    async (tempQuestion, imageUrls = []) => {
      // Check if we're sending CSAT feedback - skip loading indicator for CSAT
      const isSendingCsat = isSendingCsatRef.current;
      
      if (!isSendingCsat) {
        setIsAnswerLoading(true);
      }
      let isNewConversation = false;
      let sourcesFetching = false;
      const controller = new AbortController();
      setControl(controller);

      let tempAnswer = '';
      let question = tempQuestion;
      let tempAnswerId = uuidv4();
      let receivedResolutionAnswer = false; // Track if we received resolution_answer event
      let receivedResolutionAnswerFalse = false; // Track if we received resolution_answer = false (stream continues)
      let receivedCsatEvent = false; // Track if we received show_csat_score event
      let receivedReturnedToConversation = false; // Track if we received returned_to_conversation event
      
      // Capture resolution mode at the start
      const isResolutionMode = isResolvingMessageRef.current !== null;
      const isCsatMode = csatScoreRef.current !== null;
      const csatScore = csatScoreRef.current;
      // Capture if this is a follow-up scenario (before it gets cleared)
      const isFollowUpScenario = isResolutionFollowUpRef.current;

      let conversation = isInHumanHandoverApp ? liveAgentConversation : currentConversation;

      if (!conversation?.id) {
        const newConversation = await createNewConversation();
        if (newConversation) {
          conversation = newConversation;
          isNewConversation = true;
        }
      }

      if (conversation?.id && !isInApp && !isPreviewMode && config?.kb_id) {
        const questionText = typeof question === 'string' ? question : question?.content;
        if (questionText) {
          addConversation(config.kb_id, {
            id: conversation.id,
            dateCreated: conversation.date_created || new Date().toISOString(),
            lastMessage: questionText,
            interfaceType: interfaceType,
          });
        }
      }

      // Check for live agent interaction FIRST before checking lead gen mode
      // This ensures live agent flow takes precedence over lead generation
      if (interactingWithLiveAgent && (!urlParams.get('client_full_name') || !urlParams.get('client_email'))) {
        setShowButtons(false);

        // ✅ VALIDATE conversation for live agent flow
        if (!conversation?.id) {
          console.error('❌ [sendQuestion] No conversation ID for live agent flow');
          setInteractingWithLiveAgent(false);
          setShowButtons(true);
          setIsAnswerLoading(false);
          handleNotification({
            type: 'error',
            message: 'Failed to create conversation. Please try again.',
            duration: 5000,
          });
          return;
        }

        if (!userName && hasAskedForName) {
          // User has provided their name in response to our request
          setUserName(question.content);
          setIsAnswerLoading(false);
          // Ask for the email
          const liveAgentEmailRequestMessage = {
            id: uuidv4(),
            content: `Thank you, ${question.content}. ${
              config?.live_agent_email_prompt ||
              'Now, could you please provide your email?'
            }`,
            type: CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id) ? 'human_handover_message' : 'message',
            role: 'assistant',
            date_created: new Date(),
          };
          handleMessagesOnChat('add', liveAgentEmailRequestMessage);
        } else if (userName && !userEmail) {
          // User has provided their email in response to our request
          setUserEmail(question.content);
          connectToLiveAgent(userName, question.content, conversation);
        } else if (!hasAskedForName) {
          // This is the first message after live agent was triggered, ask for name first
          setHasAskedForName(true);
          setIsAnswerLoading(false);
          const liveAgentNameRequestMessage = {
            id: uuidv4(),
            content: config?.live_agent_name_prompt || 'Before we connect you to with a member of our team, could you please tell me your name?',
            type: CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id) ? 'human_handover_message' : 'message',
            role: 'assistant',
            date_created: new Date(),
          };
          handleMessagesOnChat('add', liveAgentNameRequestMessage);
        } else {
          // Both name and email are collected, connect to live agent
          connectToLiveAgent(userName, userEmail, conversation);
        }
        return;
      }
      
      // Only check lead gen mode if not interacting with live agent
      if (isLeadGenMode && !isInApp && !pollResponse) {
        setHideFooter(false);
        setShowButtons(false);
        setIsAnswerLoading(false);

        if (tempQuestion.content.toLowerCase() === 'skip to general chat') {
          setIsLeadGenMode(false);
          setLeadGenDeclined(true); // Mark that user declined lead gen
          // Store declined status in localStorage (dynamic based on config)
          setStoredLeadGenStatus(config?.kb_id, { completed: false, declined: true }, config);
          setShowButtons(true);
          question = savedQuestion;
          setIsAnswerLoading(true);
        } else {
          const res = await handleDynamicLeadGen(
            question.content,
            conversation.id
          );

          if (res) {
            setLeadGenInputs({
              input_id: res.next_question_input_id,
              question: res.next_question,
              input_type: res.currentField?.input_type,
              input_options: res.currentField?.input_options,
              input_label: res.currentField?.input_label,
              show_options: true,
            });
            return;
          } else {
            setIsLeadGenMode(false);
            setLeadGenCompleted(true);
            leadGenCompletedRef.current = true;
            // Store completion status in localStorage (dynamic based on config)
            setStoredLeadGenStatus(config?.kb_id, { completed: true, declined: false }, config);
            setShowButtons(true);
            question = savedQuestion;
            setIsAnswerLoading(true);
          }
        }
      }

      if(config.data_collection && !config?.show_data_collection_on_conversation_begin && !isInApp && !isPreviewMode && !leadGenDeclined && !leadGenCompleted && !leadGenCompletedRef.current) {
        const res = await evaluateRules(question.content, conversation.id, 'data_collection');
        if (res) {
          if(firstQuestion) {
            setSavedQuestion(question);
            setFirstQuestion(false);
          }
          setIsAnswerLoading(false);
          return;
        }
        setIsAnswerLoading(true);
      }

      if (
        config?.talk_to_live_agent &&
        !config?.show_live_agent_on_conversation_begin &&
        (isAgentAvailable || config.show_live_agent_rule !== '') &&
        !isInApp &&
        !isPreviewMode
      ) {
        const res = await evaluateRules(
          question.content,
          conversation.id,
          'live_agent'
        );
        if (res) {
          return;
        }
      }

      if(config?.calendly_integration_enabled && !config?.show_calendly_at_the_start && !isInApp && !isPreviewMode) {
        const res = await evaluateRules(question.content, conversation.id, 'calendly');
        if (res) {
          return;
        }
      }

      if (pollResponse && !isLiveAgentResolved) {
        setIsAnswerLoading(false);

        // Send message through WebSocket
        sendWebSocketMessage(
          question.content,
          questionIsQuickResponse,
          imageUrls || []
        );

        setQuestionIsQuickResponse(false);
        return;
      }

      setHideFooter(false);
      const messageTimestamp = new Date();
      
      // Don't add loading message if we're sending CSAT feedback
      if (!isSendingCsat) {
        const loadingMessage = {
          id_temp: tempAnswerId,
          content: '',
          type: 'normal',
          role: 'assistant',
          status: 'loading',
          reaction: '',
          date_created: messageTimestamp,
        };
        
        handleMessagesOnChat('add', loadingMessage);
      }

      const handleError = (err) => {
        // notify(
        //   'failure',
        //   `${
        //     embedMode
        //       ? 'We could not process your request. Please try again and if the issue persists, contact support for help.'
        //       : 'We could not process your request. Please try again and if the issue persists, contact us on support@dante-ai.com for help.'
        //   }`
        // );
        if (err instanceof FatalError) {
          throw err;
        } else {
          controller.abort();
          setIsAnswerLoading(false);
          throw new RetriableError();
        }
      };

      const handleMessage = (event) => {
        queryStreamOpen.current = true;
        if (isSendingCsatRef.current) {
          controller.abort();
          setIsAnswerLoading(false);
          setOnGoingStream(false);
          handleMessagesOnChat('update', {
            id_temp: tempAnswerId,
            content: '',
            status: 'complete',
          });
          return;
        }

        if (event.event === 'error') {
          // handle error here...
        }

        if (event.event === 'ping' || event.event === 'talk') return;

        if (event.event === 'message_id') {         
          // Allow message_id to be processed if resolution_answer was false (stream continues)
          if (receivedResolutionAnswer && !receivedResolutionAnswerFalse) {
            return;
          }
          
          React.startTransition(() => {
            handleMessagesOnChat('update', {
              id: event.data,
              id_temp: tempAnswerId,
              date_created: new Date(), 
            });

            setShowSources(prevSources => {
              const newSources = [...prevSources, event.data];
              return newSources;
            });
          });

          return;
        }

        if (event.event === 'resolution_answer' && customizationData?.resolution_enabled) {
          // Handle event.data as either boolean, string, or object
          let resolutionData = event.data;
          let resolutionWasHelpful = false;
          
          // Parse if event.data is a JSON string
          if (typeof event.data === 'string') {
            try {
              resolutionData = JSON.parse(event.data);
            } catch (e) {
              // Not JSON, treat as string
              resolutionData = event.data;
            }
          }
          
          // Determine if resolution was helpful
          if (typeof resolutionData === 'object' && resolutionData !== null) {
            resolutionWasHelpful = resolutionData.helpful === true || resolutionData.resolution_helpful === true;
          } else {
            const dataStr = String(resolutionData).toLowerCase();
            resolutionWasHelpful = resolutionData === true || dataStr === 'true';
          }
          
          if (isFollowUpScenario && !resolutionWasHelpful) {
            receivedResolutionAnswer = true;
            
            setIsAnswerLoading(false);
            setOnGoingStream(false);
            
            handleMessagesOnChat('update', {
              id_temp: tempAnswerId,
              content: '',
              status: 'complete',
            });
            
            const acknowledgmentMessage = {
              id: uuidv4(),
              content: ACKNOWLEDGMENT_MESSAGES[Math.floor(Math.random() * ACKNOWLEDGMENT_MESSAGES.length)],
              type: 'message',
              role: 'assistant',
              date_created: new Date(),
            };
            
            handleMessagesOnChat('add', acknowledgmentMessage);
            
            setIsInResolutionMode(false);
            isResolvingMessageRef.current = null;
            isResolutionFollowUpRef.current = false;
            
            controller.abort();
            
            return;
          }
          
          if (resolutionWasHelpful) {
            receivedResolutionAnswer = true;
            
            setIsAnswerLoading(false);
            setOnGoingStream(false);
            
            handleMessagesOnChat('update', {
              id_temp: tempAnswerId,
              content: '',
              status: 'complete',
            });
            
            const resolutionResponseMessage = {
              id: uuidv4(),
              content: RESOLUTION_RESPONSE_MESSAGES[Math.floor(Math.random() * RESOLUTION_RESPONSE_MESSAGES.length)],
              type: 'message',
              role: 'assistant',
              date_created: new Date(),
            };
            
            handleMessagesOnChat('add', resolutionResponseMessage);
            
            const followUpQuestionMessage = {
              id: uuidv4(),
              content: "Is there anything else you'd like to know?",
              type: 'message',
              role: 'assistant',
              date_created: new Date(),
            };
            
            const resolutionFollowUpMessage = {
              id: uuidv4(),
              type: 'resolution_follow_up',
              role: 'assistant',
              prompts: ["I have another question", "No, I'm all set!"],
              date_created: new Date(),
            };
            
            handleMessagesOnChat('add', followUpQuestionMessage);
            handleMessagesOnChat('add', resolutionFollowUpMessage);
            
            controller.abort();
            
            return;
          } else {
            // User indicated they need more help (resolution_answer = false)
            receivedResolutionAnswerFalse = true;
            
            // Clear any existing stream content that might have been collected before this event
            tempAnswer = '';
            
            // Update/clear the existing temp message if it exists
            handleMessagesOnChat('update', {
              id_temp: tempAnswerId,
              content: '',
              status: 'complete',
            });
            
            // Always show the empathy message FIRST
            const empathyMessage = {
              id: uuidv4(),
              content: RESOLUTION_NEGATIVE_MESSAGES[Math.floor(Math.random() * RESOLUTION_NEGATIVE_MESSAGES.length)],
              type: 'message',
              role: 'assistant',
              date_created: new Date(),
            };
            
            handleMessagesOnChat('add', empathyMessage);
            
            // Create a new tempAnswerId for the backend response that will stream after
            tempAnswerId = uuidv4();
            tempAnswer = '';
            
            const loadingMessage = {
              id_temp: tempAnswerId,
              content: '',
              type: 'normal',
              role: 'assistant',
              status: 'loading',
              reaction: '',
              date_created: new Date(),
            };
            
            handleMessagesOnChat('add', loadingMessage);
            
            // Stream will continue and backend response will be shown after the empathy message
            return;
          }
        }
        
        // Don't skip stream data if we received resolution_answer = false (stream continues)
        if (receivedResolutionAnswer && !receivedResolutionAnswerFalse) {
          return;
        }

        if (event.event === 'show_csat_score') {
          receivedCsatEvent = true;
          
          setIsAnswerLoading(false);
          setOnGoingStream(false);
          
          handleMessagesOnChat('update', {
            id_temp: tempAnswerId,
            content: '',
            status: 'complete',
          });

          // Only show CSAT if it's enabled
          if (customizationData?.csat_enabled && customizationData?.collection_method !== 'ai-analyzed') {
            setIsShowingCsat(true);

            // Show thank you message BEFORE the feedback question
            const thankYouMessage = {
              id: uuidv4(),
              content: RESOLUTION_THANK_YOU_MESSAGES[Math.floor(Math.random() * RESOLUTION_THANK_YOU_MESSAGES.length)],
              type: 'message',
              role: 'assistant',
              date_created: new Date(),
            };
            
            handleMessagesOnChat('add', thankYouMessage);

            const csatQuestionMessage = {
              id: uuidv4(),
              content: customizationData?.feedback_question || "How are we doing? I'd love to hear your thoughts!",
              type: 'message',
              role: 'assistant',
              date_created: new Date(),
            };

            const feedbackOptions = customizationData?.feedback_options || {};
            const csatOptions = [];

            for (let i = 5; i >= 1; i--) {
              const optionKey = `feedback_option_${i}`;
              const optionData = feedbackOptions[optionKey];

              if (optionData) {
                csatOptions.push({
                  icon_id: optionData.icon_id,
                  label: optionData.label,
                  value: optionData.icon_id,
                  optionNumber: i,
                });
              }
            }

            if (csatOptions.length > 0) {
              const csatFeedbackMessage = {
                id: uuidv4(),
                type: 'csat_feedback',
                role: 'assistant',
                options: csatOptions,
                date_created: new Date(),
              };

              handleMessagesOnChat('add', csatQuestionMessage);
              handleMessagesOnChat('add', csatFeedbackMessage);
            } else {
              handleMessagesOnChat('add', csatQuestionMessage);
            }
          } else {
            // CSAT is disabled but we received show_csat_score (resolution completed)
            // Show thank you message and close conversation
            const thankYouMessage = {
              id: uuidv4(),
              content: customizationData?.thank_you_message || "Thank you for your feedback!",
              type: 'message',
              role: 'assistant',
              date_created: new Date(),
            };
            
            handleMessagesOnChat('add', thankYouMessage);
            
            // Wait a bit before showing conversation closed message
            setTimeout(() => {
              const conversationClosedMessage = {
                id: uuidv4(),
                content: "This conversation has been closed. Feel free to start a new one anytime!",
                type: 'info',
                date_created: new Date(),
              };
              
              handleMessagesOnChat('add', conversationClosedMessage);
              setHideFooter(true);
              setIsShowingCsat(false);
              setIsInResolutionMode(false);
            }, 600);
          }
          
          controller.abort();
          
          return;
        }
        
        if (receivedCsatEvent) {
          return;
        }

        if (event.data === '_LOADING_IMAGE_START_ ') {
          setChatImageLoader(true);
        } else if (event.data === '_LOADING_IMAGE_END_') {
          setChatImageLoader(false);
        } else if (event.data === '_STREAMING_FINISHED_') {
          setSourcesReady(true);
          controller.abort();
          
          // If we already received resolution_answer (true) or show_csat_score, don't create/update the answer message
          // But if resolution_answer was false, allow the streamed response to be saved
          if ((receivedResolutionAnswer && !receivedResolutionAnswerFalse) || receivedCsatEvent) {
            finishGeneratingAnswer(conversation.id, isNewConversation);
            setMessageLoaded(true);
            return;
          }
          
          const answer = {
            id_temp: tempAnswerId,
            content: tempAnswer,
            status: 'complete',
            role: 'assistant',
            reaction: '',
            date_created: messageTimestamp, // Preserve the original timestamp
          };
          handleMessagesOnChat('update', answer);
          finishGeneratingAnswer(conversation.id, isNewConversation);
          setMessageLoaded(true);
          
          // If returned_to_conversation event was received, show resolution feedback immediately
          if (receivedReturnedToConversation) {
            const delay = calculateReadingDelay(tempAnswer);
            
            setTimeout(() => {
              // Get the actual message ID and show resolution popup
              setMessages((currentMessages) => {
                const actualMessage = currentMessages.find(
                  (msg) => msg.id_temp === tempAnswerId && msg.id
                );
                
                if (actualMessage?.id) {
                  resolvedMessagesRef.current[actualMessage.id] = {
                    messageId: actualMessage.id,
                    messageContent: tempAnswer,
                    tempId: tempAnswerId,
                    timestamp: Date.now()
                  };
                  showResolutionPopup(actualMessage.id);
                }
                
                return currentMessages;
              });
            }, delay);
          } else if (!receivedResolutionAnswer) {
            scheduleResolutionCheck(tempAnswerId, tempAnswer);
          }
        } else if (event.data === '_STARTING_SOURCES_FETCHING_') {
          // Sources fetching started
        } else if (!sourcesFetching) {
          // Allow stream to continue if resolution_answer was false
          if (receivedResolutionAnswer && !receivedResolutionAnswerFalse) {
            return;
          }
          const parsedData = formatEventData(event.data);
          tempAnswer += parsedData;
          handleMessagesOnChat('update', {
            id_temp: tempAnswerId,
            content: tempAnswer,
            status: 'typing',
            role: 'assistant',
            reaction: '',
            date_created: new Date(), // Add timestamp for each content update
          });
        }
      };

      const handleOpenStream = (res) => {
        if (res.status === 426) {
          setSaveSignUpBtn(true);
          window.parent.postMessage(
            {
              eventType: 'signup-btn',
              eventData: true,
            },
            '*'
          );
        }
        if (res.ok && res.status === 200) {
        } else if (
          res.status >= 400 &&
          res.status < 500 &&
          res.status !== 429
        ) {
          if (res.status === 422) {
            // notify('failure', 'Could not process your request.');
          }
          if (res.status === 402) {
            // notify('failure', 'Monthly credit limit exceeded. Top up using credits top-up button.');
          } else if (res.status === 403) {
            // notify('failure', 'Upgrade your plan to use GPT-4.');
          } else if (res.status === 400) {
            // notify('failure', 'Retrain your untrained chatbot.');
          }
        }
      };
      const handleCloseStream = () => {
        control.abort();
      };

      const messageHandlers = {
        onMessage: handleMessage,
        onOpen: handleOpenStream,
        onClose: handleCloseStream,
        onError: handleError,
      };

      try {
        setMessageLoaded(false);
        // Don't set onGoingStream if we're sending CSAT feedback
        if (!isSendingCsat) {
          setOnGoingStream(true);
        }
        // Use selectedChatbot.llmModel first, then fall back to last_model_used, then fall back to default
        const llmModel = selectedChatbot?.llmModel?.value || selectedChatbot?.last_model_used?.value || urlParams.get('modeltype') || LLM_MODEL_DEFAULT.value;
        
        // Get resolution message ID for payload
        const resolutionMessageId = isResolvingMessageRef.current;
        // Check if we should send returned_to_conversation (only on first resolution answer after returning)
        const shouldSendReturnedToConversation = shouldSendReturnedToConversationRef.current && isResolutionMode;
        const conversationPayload = {
          ...conversation,
          llmModel,
          question: question.content,
          messages: messages.slice(-5),
          resolution_mode: isResolutionMode,
          resolution_message_id: isResolutionMode ? resolutionMessageId : undefined,
          csat_score: isCsatMode ? `feedback_option_${csatScore}` : undefined,
          returned_to_conversation: shouldSendReturnedToConversation ? true : undefined,
        };
        
        // Clear the returned_to_conversation flag after use (only send on first resolution answer)
        if (shouldSendReturnedToConversation) {
          shouldSendReturnedToConversationRef.current = false;
        }
        
        // Clear the resolution flag after use
        if (isResolutionMode) {
          isResolvingMessageRef.current = null;
        }
        
        // Clear the CSAT score after use
        if (isCsatMode) {
          csatScoreRef.current = null;
        }

        if (isInApp && !isPreviewMode) {
          await sendMessageInApp({
            conversation: conversationPayload,
            accessToken: config?.access_token,
            ...messageHandlers,
            control: controller,
          });
          const creditsResponse = await userService.getUserCredits();
          if (creditsResponse.status === 200) {
            setUserData({
              ...userData,
              credits_key: {
                ...creditsResponse.data,
              },
            });
          }
        } else if (!isInApp && !isPreviewMode) {
          await sendMessageEmbed({
            conversation: conversationPayload,
            accessToken: config?.token,
            ...messageHandlers,
            control: controller,
            cookie: cookie,
            userID: getSessionUserId(config?.kb_id),
          });
        }
      } catch (error) {
        console.error('Error sending message:', error);
        setOnGoingStream(false);
      }
    },
    [
      currentConversation,
      isInApp,
      config,
      messages,
      setCurrentConversation,
      createNewConversation,
      interactingWithLiveAgent,
      isLeadGenMode,
      questionIsQuickResponse,
      savedQuestion,
      cookie,
      scheduleResolutionCheck,
    ]
  );

  /**
   * Handles resolution feedback from user
   * @param {string} messageId - The message ID (the related message, not the feedback message)
   * @param {boolean} wasHelpful - Whether the user found it helpful
   * @param {string} labelText - The label text from the button clicked
   */
  const handleResolutionFeedback = useCallback(
    async (messageId, wasHelpful, labelText) => {
      try {
        setIsInResolutionMode(true);
        
        // Send the Yes/No as a message with resolution_mode
        isResolvingMessageRef.current = messageId;
        
        const userResponseMessage = {
          id: uuidv4(),
          content: labelText || (wasHelpful ? 'Yes' : 'No'), // Use labelText if provided, fallback to Yes/No
          role: 'user',
          type: 'message',
          date_created: new Date(),
        };
        
        // Add user message
        handleMessagesOnChat('add', userResponseMessage);
        
        // // If user clicked "No", immediately show empathy message BEFORE the loading starts
        // if (!wasHelpful) {
        //   const empathyMessage = {
        //     id: uuidv4(),
        //     content: RESOLUTION_NEGATIVE_MESSAGES[Math.floor(Math.random() * RESOLUTION_NEGATIVE_MESSAGES.length)],
        //     type: 'message',
        //     role: 'assistant',
        //     date_created: new Date(),
        //   };
          
        //   handleMessagesOnChat('add', empathyMessage);
        // }
        
        // Mark resolution feedback as answered
        setMessages((prev) => {
          return prev.map(msg => {
            if (msg.type === 'resolution_feedback' && msg.related_message_id === messageId) {
              return { ...msg, answered: true };
            }
            return msg;
          });
        });
        
        // Send the message with resolution_mode (this will clear isResolvingMessageRef.current)
        await sendQuestion(userResponseMessage);
      } catch (error) {
        console.error('❌ Failed to submit resolution feedback:', error);
      }
    },
    [sendQuestion, handleMessagesOnChat]
  );

  /**
   * Dismisses the resolution feedback without submitting feedback
   */
  const handleDismissResolution = useCallback((messageId) => {
    
    // Remove the resolution feedback message from the array
    setMessages((prev) => prev.filter(msg => 
      !(msg.type === 'resolution_feedback' && msg.related_message_id === messageId)
    ));
    
    // Remove from resolved messages so it doesn't show again
    delete resolvedMessagesRef.current[messageId];
  }, []);

  /**
   * Handles CSAT feedback from user
   * @param {number} optionNumber - The feedback option number (1-5)
   * @param {string} label - The feedback label
   * @param {string} iconId - The icon ID for the emoji
   */
  const handleCsatFeedback = useCallback(
    async (optionNumber, label, iconId) => {
      try {
        csatScoreRef.current = optionNumber;
        isSendingCsatRef.current = true;
        
        setMessages((prev) => {
          return prev.map(msg => {
            if (msg.type === 'csat_feedback') {
              return { ...msg, answered: true };
            }
            return msg;
          });
        });
        
        await new Promise(resolve => setTimeout(resolve, 200));
        
        const userResponseMessage = {
          id: uuidv4(),
          content: label, 
          role: 'user',
          type: 'message',
          date_created: new Date(),
          csat_icon_id: iconId, 
          csat_label: label, 
        };
        
        // Add user message
        handleMessagesOnChat('add', userResponseMessage);
        
        await sendQuestion(userResponseMessage);
        
        isSendingCsatRef.current = false;
        
        setIsAnswerLoading(false);
        setOnGoingStream(false);
        
        await new Promise(resolve => setTimeout(resolve, 300));        
        const thankYouMessage = {
          id: uuidv4(),
          content: customizationData?.thank_you_message || "Thank you for your feedback!",
          type: 'message',
          role: 'assistant',
          date_created: new Date(),
        };
        
        // Add thank you message
        handleMessagesOnChat('add', thankYouMessage);
        
        await new Promise(resolve => setTimeout(resolve, 600));
        
        const conversationClosedMessage = {
          id: uuidv4(),
          content: "This conversation has been closed. Feel free to start a new one anytime!",
          type: 'info',
          date_created: new Date(),
        };
        
        handleMessagesOnChat('add', conversationClosedMessage);
        
        setHideFooter(true);
        setIsShowingCsat(false);
        setIsInResolutionMode(false);
      } catch (error) {
        console.error('❌ Failed to submit CSAT feedback:', error);
      }
    },
    [sendQuestion, handleMessagesOnChat, customizationData]
  );

  /**
   * Handles sending a new question.
   *
   * @param {string} question - The question to send to the assistant.
   */
  const handleSendQuestion = useCallback(
    async (question, imageUrls = []) => {      
      // Check if user is responding to an unanswered resolution feedback message BEFORE cancelling
      const hasUnansweredResolutionFeedback = messages.some(msg => 
        msg.type === 'resolution_feedback' && !msg.answered
      );
      
      // Check if user is responding to an unanswered resolution follow-up
      const hasUnansweredResolutionFollowUp = messages.some(msg => 
        msg.type === 'resolution_follow_up' && !msg.answered
      );
      
      // Check if user is responding to CSAT feedback
      const hasUnansweredCsatFeedback = messages.some(msg => 
        msg.type === 'csat_feedback' && !msg.answered
      );
      
      if (hasUnansweredResolutionFeedback) {
        // Find the most recent unanswered resolution feedback
        const unansweredResolution = messages
          .filter(msg => msg.type === 'resolution_feedback' && !msg.answered)
          .pop();
        
        if (unansweredResolution) {
          setIsInResolutionMode(true);
          isResolvingMessageRef.current = unansweredResolution.related_message_id;
          
          // Mark the resolution feedback as answered
          // The empathy message will be shown based on the backend resolution_answer event
          handleMessagesOnChat('update', {
            ...unansweredResolution,
            answered: true,
          });
        }
      } else if (hasUnansweredResolutionFollowUp) {
        
        // Mark the resolution_follow_up message as answered to hide the prompts
        setMessages((prev) => {
          return prev.map(msg => {
            if (msg.type === 'resolution_follow_up') {
              return { ...msg, answered: true };
            }
            return msg;
          });
        });
        
        const answeredResolution = messages
          .filter(msg => msg.type === 'resolution_feedback' && msg.answered)
          .pop();
        
        if (answeredResolution && answeredResolution.related_message_id) {
          setIsInResolutionMode(true);
          isResolvingMessageRef.current = answeredResolution.related_message_id;
          // Set flag to indicate this is a follow-up scenario
          isResolutionFollowUpRef.current = true;
        }
      } else if (hasUnansweredCsatFeedback) {
        setMessages((prev) => {
          return prev.map(msg => {
            if (msg.type === 'csat_feedback') {
              return { ...msg, answered: true };
            }
            return msg;
          });
        });
        
        // Mark CSAT as answered (thank you message already shown before feedback question)
        setIsShowingCsat(false);
        setIsInResolutionMode(false);
      } else if (!hasUnansweredResolutionFollowUp && !hasUnansweredCsatFeedback && !isResolvingMessageRef.current) {
        setIsInResolutionMode(false);
        // Clear the follow-up flag when exiting resolution mode
        isResolutionFollowUpRef.current = false;
      }
      
      cancelAllPendingResolutions();
      setUserIsTyping(false); // Reset typing state
      
      resolvedMessagesRef.current = {};

      if(customizationData?.blocked_words?.length > 0) {
       
        const foundBlockedWords = customizationData.blocked_words.filter(word => {
        
          const wordPattern = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
          return wordPattern.test(question);
        });

        if (foundBlockedWords.length > 0) {
          // Show warning notification with all blocked words found
          const message = foundBlockedWords.length === 1
            ? `Your message contains a blocked word: "${foundBlockedWords[0]}". Please remove it and try again.`
            : `Your message contains blocked words: ${foundBlockedWords.map(word => `"${word}"`).join(', ')}. Please remove them and try again.`;

          handleNotification({
            type: 'alert',
            message,
            duration: 5000,
          });

          return;
        }
      }
      setHasStartedConversation(true);
      
      // Hide select options when a message is sent during lead generation
      if (isLeadGenMode && leadGenInputs?.input_type === 'select') {
        setLeadGenInputs(prev => ({
          ...prev,
          show_options: false
        }));
      }
      
      // Use the hook's markAsShown instead of the old logic
      markAsShown();

      const questionId = uuidv4();
      const dateCreated = new Date();
      const newQuestion = {
        id: questionId,
        content: question,
        role: isInHumanHandoverApp ? 'assistant' : 'user',
        date_created: dateCreated,
        images: imageUrls,
        type:
          isInHumanHandoverApp && questionIsQuickResponse
            ? 'quick_response'
            : 'message',
        // Mark if message was sent during live agent interaction
        sentDuringLiveAgent: pollResponse || interactingWithLiveAgent,
      };
      if(isInHumanHandoverApp) {
        console.log('liveAgentData', liveAgentData)
        newQuestion.agent_name = liveAgentData?.name;
        newQuestion.agent_title = liveAgentData?.agent_title;
        newQuestion.agent_profile_pic = liveAgentData?.image;
      }
      handleMessagesOnChat('add', newQuestion);
      
      // Check if we're in live agent mode first - this takes precedence over lead gen
      if (interactingWithLiveAgent) {
        // Skip lead gen flow if we're interacting with a live agent
        await sendQuestion(newQuestion, imageUrls);
        return;
      }
      

      
      // Only proceed with lead gen if not interacting with live agent
      if (
        isLeadGenMode &&
        firstQuestion &&
        !isInApp &&
        !isInHumanHandoverApp &&
        !pollResponse
      ) {
        setIsAnswerLoading(false);
        setIsLeadGenMode(true);
        setSavedQuestion(newQuestion);
        setFirstQuestion(false);
        setShowButtons(false);
        if(config?.is_data_collection_optional){
          setHideFooter(true);
          setShowConsent(true);
          handleMessagesOnChat('add', {
            id: uuidv4(),
            content:
              config?.data_collection_consent_text ||
              DEFAULT_CHAT_MESSAGES.CONSENT_MESSAGE,
            type: 'message',
            role: 'assistant',
          });
          return;
        }
      }
      await sendQuestion(newQuestion, imageUrls);
    },
    [
      cancelAllPendingResolutions,
      markAsShown, 
      isInHumanHandoverApp, 
      questionIsQuickResponse, 
      liveAgentData, 
      interactingWithLiveAgent, 
      isLeadGenMode, 
      firstQuestion, 
      isInApp, 
      pollResponse, 
      config?.is_data_collection_optional, 
      config?.data_collection_consent_text, 
      sendQuestion,
      messages,
      handleMessagesOnChat,
      customizationData
    ]
  );

  const handleNotification = ({ type, message, duration }) => {
    setNotification({
      show: true,
      type,
      message,
    });

    if (duration) {
      setTimeout(() => {
        setNotification({
          show: false,
        });
      }, duration);
    }
  };

  const resetConversation = useCallback(() => {
    setMessages(
      config?.initial_messages || customizationData?.initial_messages || []
    );

    // Use the hook's resetPrompts instead of the old logic
    resetPrompts();
    setHasStartedConversation(false);
    setShowConsent(false);
    setPlayed([]);
    setHideFooter(false); // Reset footer visibility

    // Reset live agent states
    setInteractingWithLiveAgent(false);
    setUserName('');
    setUserEmail('');
    setHasAskedForName(false);

    // Reset resolution and CSAT states and refs
    setIsInResolutionMode(false);
    setIsShowingCsat(false);
    isResolvingMessageRef.current = null;
    isResolutionFollowUpRef.current = false;
    csatScoreRef.current = null;
    isSendingCsatRef.current = false;
    shouldSendReturnedToConversationRef.current = false;
    resolvedMessagesRef.current = {};
    resolutionTimeoutsRef.current = {};
    if (stoppedTypingTimeoutRef.current) {
      clearTimeout(stoppedTypingTimeoutRef.current);
      stoppedTypingTimeoutRef.current = null;
    }

    // Reset the message loading flag
    hasLoadedMessages.current = false;

    // Disconnect the WebSocket
    disconnectWebSocket();
  }, [resetPrompts, disconnectWebSocket]);

  const handleCloseChat = () => {
    // Disconnect the WebSocket before clearing messages
    disconnectWebSocket();
    
    // Reset connection tracking
    activeConnectionIdRef.current = null;
    
    setMessages([]);
    setShouldFetchCustomization(true);
    hasLoadedMessages.current = false;
    setHideFooter(false); // Reset footer visibility
    
    // Reset resolution and CSAT states and refs
    setIsInResolutionMode(false);
    setIsShowingCsat(false);
    isResolvingMessageRef.current = null;
    isResolutionFollowUpRef.current = false;
    csatScoreRef.current = null;
    isSendingCsatRef.current = false;
    shouldSendReturnedToConversationRef.current = false;
    resolvedMessagesRef.current = {};
    resolutionTimeoutsRef.current = {};
    if (stoppedTypingTimeoutRef.current) {
      clearTimeout(stoppedTypingTimeoutRef.current);
      stoppedTypingTimeoutRef.current = null;
    }
    
    // Call the parent's close button handler if provided
    handleCloseButton && handleCloseButton();
  };
  /**
   * Loads and processes messages for a specific conversation by its ID.
   * Supports both standard and live agent conversations.
   *
   * @param {string} conversation_id - The ID of the conversation to load messages from.
   * @param {boolean} isLiveAgent - Determines whether to fetch messages from live agent service.
   * @returns {Promise<void>} A promise that resolves when the messages are loaded and processed.
   */
  const loadConversationMessages = async (
    conversation_id,
    isLiveAgent = false
  ) => {
    try {
      
      // Set loading state to true
      setMessagesLoading(true);
      // Fetch the actual conversation messages
      let conversation;
      if (isLiveAgent) {
        const response = await humanHandoverService.getMessagesByConversationId(
          conversation_id,
          timezone
        );
        conversation = response.data;
      } else if (isInApp) {
        const response = await messagesService.getMessagesByConversationId(
          conversation_id,
          timezone
        );
        conversation = response.data;
      } else if (!isInApp && !isPreviewMode && !isInHumanHandoverApp) {
        const response = await messagesService.getSharedMessagesByConversationId(
          conversation_id,
          config?.token
        );
        conversation = response.data;
      }

      if(!isLiveAgent && !isInApp) {
        const { data: conversationMode } = await humanHandoverService.getConversationMode(conversation_id, config?.token);
        
        if (conversationMode.conversation_mode === 'human_handover') {
          setIsPreviousConversationHasLiveAgent(true);
          
          // Check if conversation is resolved before reconnecting
          if (conversationMode.state !== 'resolved') {
            pollAgentTakeover(conversationMode.hh_conversation_id);
            setIsHumanHandover && setIsHumanHandover(true);
            setIsLiveAgentResolved(false); // Ensure resolved state is properly set
          } else {
            setIsLiveAgentResolved(true);
          }
          
          setAgentName(conversationMode.agent_name);
          setAgentTitle(conversationMode.agent_title);
          setAgentAvatar(conversationMode.agent_image);
          setAgentJoined(conversationMode.agent_joined);
        }
      }

      // Choose the appropriate transformation function
      const transformFn = isLiveAgent
        ? transformMessageToBubbleMessageHumanHandover
        : transformMessageToBubbleMessage;

      // Transform the conversation results
      const conversationMessages = conversation.results.flatMap(transformFn);


      const initialMessages = config?.initial_messages || customizationData?.initial_messages || [];
      let allMessages = [...initialMessages, ...conversationMessages];
      
      // Check resolution and CSAT completion status
      const csatCompleted = conversation.csat_completed || false;
      const resolutionCompleted = conversation.resolution_completed || false;
      const csatScore = conversation.csat_score || null;
      
      // Only show CSAT history if it's enabled
      if (csatScore && customizationData?.csat_enabled) {
        let optionNumber = null;
        if (typeof csatScore === 'string') {
          const optionMatch = csatScore.match(/feedback_option_(\d+)/);
          if (optionMatch) {
            optionNumber = parseInt(optionMatch[1], 10);
          } else {
            const parsed = parseInt(csatScore, 10);
            if (!isNaN(parsed)) {
              optionNumber = parsed;
            }
          }
        } else if (typeof csatScore === 'number') {
          optionNumber = csatScore;
        }


        if (optionNumber) {
          const feedbackOptions = customizationData?.feedback_options || {};
          const optionKey = `feedback_option_${optionNumber}`;
          const optionData = feedbackOptions[optionKey];


          if (optionData) {
            const csatQuestionMessage = {
              id: uuidv4(),
              content: customizationData?.feedback_question || "How are we doing? I'd love to hear your thoughts!",
              type: 'message',
              role: 'assistant',
              date_created: new Date(),
            };

            const csatUserResponseMessage = {
              id: uuidv4(),
              content: optionData.label,
              role: 'user',
              type: 'message',
              date_created: new Date(),
              csat_icon_id: optionData.icon_id,
              csat_label: optionData.label,
            };

            allMessages = [...allMessages, csatQuestionMessage, csatUserResponseMessage];
          }
        }
      }
      
      // Scenario 1: Both completed - show conversation finished message and hide input
      if (csatCompleted) {        
        // Add conversation closed message
        const conversationClosedMessage = {
          id: uuidv4(),
          content: "This conversation has been closed. Feel free to start a new one anytime!",
          type: 'info',
          date_created: new Date(),
        };
        
        allMessages = [...allMessages, conversationClosedMessage];
        setHideFooter(true);
        setIsShowingCsat(false);
        setIsInResolutionMode(false);
      }
      // Scenario 2: Resolution completed but CSAT not - show CSAT feedback (only if enabled)
      else if (!csatCompleted && resolutionCompleted && customizationData?.csat_enabled) {

        // Set CSAT state to hide suggested prompts
        setIsShowingCsat(true);
        setIsInResolutionMode(false);
        
        // Create the CSAT question message
        const csatQuestionMessage = {
          id: uuidv4(),
          content: customizationData?.feedback_question || "How are we doing? I'd love to hear your thoughts!",
          type: 'message',
          role: 'assistant',
          date_created: new Date(),
        };
        
        // Create the CSAT feedback message with options
        const feedbackOptions = customizationData?.feedback_options || {};
        const csatOptions = [];
        
        // Map backend format to array (feedback_option_5 -> feedback_option_1)
        for (let i = 5; i >= 1; i--) {
          const optionKey = `feedback_option_${i}`;
          const optionData = feedbackOptions[optionKey];
          
          if (optionData) {
            csatOptions.push({
              icon_id: optionData.icon_id,
              label: optionData.label,
              value: optionData.icon_id,
              optionNumber: i,
            });
          }
        }
        
        // Add CSAT messages
        allMessages = [...allMessages, csatQuestionMessage];
        
        if (csatOptions.length > 0) {
          const csatFeedbackMessage = {
            id: uuidv4(),
            type: 'csat_feedback',
            role: 'assistant',
            options: csatOptions,
            date_created: new Date(),
          };
          
          allMessages = [...allMessages, csatFeedbackMessage];
        }
      }
      // Scenario 2b: Resolution completed but CSAT disabled - show thank you and close conversation
      else if (!csatCompleted && resolutionCompleted && !customizationData?.csat_enabled) {
        const thankYouMessage = {
          id: uuidv4(),
          content: customizationData?.thank_you_message || "Thank you for your feedback!",
          type: 'message',
          role: 'assistant',
          date_created: new Date(),
        };
        
        const conversationClosedMessage = {
          id: uuidv4(),
          content: "This conversation has been closed. Feel free to start a new one anytime!",
          type: 'info',
          date_created: new Date(),
        };
        
        allMessages = [...allMessages, thankYouMessage, conversationClosedMessage];
        setHideFooter(true);
        setIsShowingCsat(false);
        setIsInResolutionMode(false);
      }
      // Scenario 3: Neither completed - normal message loading
      // Resolution will be handled on first stream response with returned_to_conversation flag
      else if (!csatCompleted && !resolutionCompleted) {
       setIsShowingCsat(false);
        setIsInResolutionMode(false);
        // Set flag to send returned_to_conversation on the first message after returning
        shouldSendReturnedToConversationRef.current = true;
      }
      
      setMessages(allMessages);

      if (!isLiveAgent) {
        setIsLeadGenMode(false);
        // Don't reset lead gen declined flag - preserve localStorage state
        // setLeadGenDeclined(false); // Removed to maintain persistence
        setShowConsent(false);
        setShowSources(conversation.results?.map((result) => result.id) || []);
      }

      // Set loading state to false after messages are loaded
      setTimeout(() => {
        setMessagesLoading(false);
      }, 300);
    } catch (error) {
      console.error('Error loading conversation messages:', error);
      setMessagesLoading(false);
    }
  };

  // Handle Live Agent
  const handleOpenLiveAgent = async () => {
    markAsShown();

    setShowButtons(false);
    setInteractingWithLiveAgent(true);
    setIsAnswerLoading(false);

    // Explicitly disable lead generation mode when switching to live agent
    setIsLeadGenMode(false);
    // Don't reset lead gen declined flag - preserve localStorage state when switching to live agent
    // setLeadGenDeclined(false); // Removed to maintain persistence
    setShowConsent(false);
    setHideFooter(false);

    //if there is no conversation id create one
    let conversation = currentConversation;
    if (!currentConversation?.id) {
      conversation = await createNewConversation();
    }

    // ✅ VALIDATE conversation before proceeding
    if (!conversation?.id) {
      setInteractingWithLiveAgent(false);
      setShowButtons(true);
      handleNotification({
        type: 'error',
        message: 'Failed to create conversation. Please try again.',
        duration: 5000,
      });
      return;
    }

    if (!urlParams.get('client_full_name') || !urlParams.get('client_email')) {
      setHasAskedForName(true);
      const liveAgentNameRequestMessage = {
        id: uuidv4(),
        content: config?.live_agent_name_prompt || 'Before we connect you to with a member of our team, could you please tell me your name?',
        type: CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id) ? 'human_handover_message' : 'message',
        role: 'assistant',
        reaction: '',
        date_created: new Date(),
      };
      handleMessagesOnChat('add', liveAgentNameRequestMessage);
    } else {
      connectToLiveAgent(urlParams.get('client_full_name'), urlParams.get('client_email'), conversation);
    }
  };

  const handleUpdateReaction = async ({ message_id, action }) => {
    try {
      if (!message_id) {
        throw new Error('Invalid message_id for updating reaction');
      }

      await messagesService.updateReaction(message_id, action);
      

      const message = messages.find((msg) => msg.id === message_id || msg.id_temp === message_id);

      if (action && action !== 'no_reaction') {
        handleNotification({
          type: action === 'thumbs_up' ? 'positive' : 'negative',
          message:
            action === 'thumbs_up'
              ? 'Response rated as helpful'
              : 'Response rated as not helpful',
          duration: 3000,
        });
      } else {
        handleNotification({
          type: 'neutral',
          message: 'Reaction removed',
          duration: 3000,
        });
      }

      handleMessagesOnChat('update', {
        ...message,
        reaction: action,
      });
    } catch (error) {
      console.error(
        `Error updating reaction for message_id=${message_id}, action=${action}:`,
        error
      );
    }
  };

  const ttsPlayHandler = async (index, playAudioFn, token = null) => {
    if (played.includes(index)) return;

    try {
      const { data } = token ? await playAudioFn(token) : await playAudioFn();
      if (!data) {
        setPlayed((prev) => [...prev, index]);
      }
    } catch (error) {
      console.error('Error playing audio TTS:', error);
    }
  };

  const ttsPlay = (index) => ttsPlayHandler(index, ttsService.playAudioTTS);

  const ttsPlayShared = (index, token) =>
    ttsPlayHandler(index, ttsService.playSharedAudioTTS, token);

  const handleSpeech = (answer, index, message) => {
    const synth = window.speechSynthesis;

    if (speechPlaying && speechPlayingID !== index) {
      synth.cancel();
    }

    if (!speechPlaying || (speechPlaying && speechPlayingID !== index)) {
      const voice = voices.find(
        (v) =>
          (v.lang === 'en-US' || v.lang === 'en-GB') &&
          v.name === 'Google UK English Female'
      );
      const speed = 1.05;
      const msg = new SpeechSynthesisUtterance(answer);
      msg.voice = voice || null;
      msg.rate = speed;

      const buffer = setInterval(() => {
        synth.pause();
        synth.resume();
      }, 5000);

      setSpeechPlaying(true);
      setSpeechPlayingID(index);
      let speakDuration;
      msg.onstart = () => {
        setNotification({
          show: true,
          type: 'alert',
          message: 'Reading message',
        });
      };
      msg.onend = (event) => {
        setSpeechPlaying(false);
        setSpeechPlayingID(null);
        clearInterval(buffer);
        setNotification({
          show: false,
        });
        speakDuration = event.elapsedTime / speed;
      };

      synth.speak(msg);

      if (!isInApp) {
        ttsPlayShared(index, config?.token);
      } else {
        ttsPlay(index);
      }
    } else {
      setSpeechPlaying(false);
      setSpeechPlayingID(null);
      synth.cancel();
      setNotification({
        show: false,
      });
      handleMessagesOnChat('update', {
        ...message,
        reaction: 'stop_message',
      });
    }
  };

  const handlePlayMessage = ({ message_id }) => {
    if (!message_id) {
      console.error('Missing message_id for playing message');
      return;
    }

    const message = messages.find((msg) => msg.id === message_id || msg.id_temp === message_id);

    if (message) {
      handleMessagesOnChat('update', {
        ...message,
        reaction: 'play_message',
      });
      handleSpeech(message.content, message.id || message.id_temp, message);
    } else {
      console.error('Message not found for TTS:', message_id);
    }
  };

  const handleSources = async ({ message_id }) => {
    if (!featureCheck('sources')) {
      return;
    }
    setSourcesLoading([...sourcesLoading, message_id]);
    try {
      const response = await modelService.getSources(message_id);

      if (response.status === 200) {
        // Set sources even if null or empty, modal will show "No sources found"
        setSources(response.data || []);
        setOpenSources(true);
        setSourcesLoading([]);
      } else {
        // If response status is not 200, still open modal with empty sources
        setSources([]);
        setOpenSources(true);
        setSourcesLoading([]);
      }
    } catch (error) {
      console.error('Error fetching sources:', error);
      // On error, still open modal to show "No sources found"
      setSources([]);
      setOpenSources(true);
      setSourcesLoading([]);
    }
  };

  const handleFooterButton = ({ message_id, action }) => {
    if (!message_id) {
      console.error('Invalid parameters for footer button action');
      return;
    }

    const actionHandlers = {
      thumbs_up: () => handleUpdateReaction({ message_id, action }),
      thumbs_down: () => handleUpdateReaction({ message_id, action }),
      play_message: () => handlePlayMessage({ message_id }),
      stop_message: () => handlePlayMessage({ message_id }),
      sources: () => handleSources({ message_id }),
      no_reaction: () =>
        handleUpdateReaction({ message_id, action: 'no_reaction' }),
    };

    const handler = actionHandlers[action];
    if (handler) {
      handler();
    } else {
      console.warn(`Unhandled action: ${action}`);
    }
  };

  useEffect(() => {
    // Only fetch data if the message is loaded and it's not in app
    // is necessary to get the id of the message to update the reaction
    if ( messageLoaded) {
      const fetchData = async () => {
        try {
           const responseMessages = isInApp ? await messagesService.getMessagesByConversationId(
            currentConversation.id,
          ) : await messagesService.getSharedMessagesByConversationId(
              currentConversation.id,
              config?.token
            );

          if (responseMessages?.data) {
            const data = responseMessages.data;
            const results = data.results;
            const lastMessage = results.length > 0 && results[results.length - 1].answer 
              ? results[results.length - 1].answer 
              : null;
            
            if (lastMessage) {
              addConversation(config?.kb_id, {
                id: currentConversation.id,
                dateCreated: currentConversation.date_created,
                lastMessage: lastMessage,
                interfaceType: interfaceType,
              });
            }
          }
        } catch (error) {
          console.error('Error fetching data:', error);
        }
      };

      fetchData();
    }
  }, [messageLoaded]);

  useEffect(() => {
    if (shouldFetchCustomization && config?.kb_id && !isPreviewMode && (!customizationData?.id && isInHumanHandoverApp)) {
      // Call the API only when shouldFetch is true
      const fetchCustomization = async () => {
        try {
          const customizationFetchFn = isInApp
            ? customizationService.getChatbotCustomizationById
            : customizationService.getSharedChatbotCustomizationById;

          const { data: customizationResponse } = await customizationFetchFn({
            kb_id: config?.kb_id,
            ...(!isInApp && { token: config?.token }),
          });
          
          setCustomizationData(customizationResponse);
          
          setShouldFetchCustomization(false);
        } catch (e) {
          console.error('Error fetching customization:', e);
        }
      };
      fetchCustomization();
    }
  }, [
    shouldFetchCustomization,
    isInApp,
    config?.id,
    config?.access_token,
    isInHumanHandoverApp
  ]);

  // Check for conversation_id in URL params and load it if present
  useEffect(() => {
    const conversationIdFromUrl = urlParams.get('conversation_id');

  
    if (conversationIdFromUrl && !currentConversation?.id && !isPreviewMode) {
      console.log('Loading conversation from URL:', conversationIdFromUrl);
      setCurrentConversation({
        id: conversationIdFromUrl,
        type: 'opening'
      });
    }
  }, [urlParams, isPreviewMode, currentConversation?.id, setCurrentConversation]);

  useEffect(() => {
    // Generate a unique key for this chatbot and interface combination
    const sessionKey = `dante-conversation-${config?.kb_id}-${interfaceType}`;

    // Check if this is a fresh page load/re-embed vs tab switch
    const isFreshLoad = () => {
      // Use a more specific session storage key that includes chatbot ID and interface type
      const lastAccessTime = sessionStorage.getItem(sessionKey);
      const currentTime = Date.now();

      // If there's no last access time or it was more than 30 minutes ago, consider it a fresh load
      if (!lastAccessTime || (currentTime - parseInt(lastAccessTime, 10)) > 30 * 60 * 1000) {
        sessionStorage.setItem(sessionKey, currentTime.toString());
        return true;
      }

      // Update the last access time
      sessionStorage.setItem(sessionKey, currentTime.toString());
      return false;
    };

    // Check if there's a conversation_id in URL params
    const conversationIdFromUrl = urlParams.get('conversation_id');

    // Only reset conversation on fresh load, not on tab switches,
    // and only if we're not explicitly opening a conversation or loading from URL
    if (isFreshLoad() && currentConversation.type !== 'opening' && !conversationIdFromUrl) {
      resetCurrentConversation();
    }

    // Handle page visibility changes (when user switches tabs and comes back)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isInApp && !isPreviewMode) {
        // Check if we need to reconnect
        if (currentConversation?.id && 
            isPreviousConversationHasLiveAgent && 
            !isWebSocketConnected && 
            !isLiveAgentResolved) {
          // Only reconnect if we have a conversation ID and it's not resolved
          humanHandoverService.getConversationMode(currentConversation.id, config?.token)
            .then(response => {
              const conversationMode = response.data;
              if (conversationMode.conversation_mode === 'human_handover' && 
                  conversationMode.state !== 'resolved') {
                pollAgentTakeover(conversationMode.hh_conversation_id);
              }
            })
            .catch(error => {
              console.error('Error checking conversation mode:', error);
            });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [config?.kb_id, interfaceType, resetCurrentConversation, currentConversation.type]);

  useEffect(() => {
    if (
      currentConversation.id === null &&
      currentConversation.type === null &&
      customizationData &&
      !isPreviewMode &&
      !isInHumanHandoverApp
    ) {
      resetConversation();
    } else if (
      currentConversation.id !== null &&
      currentConversation.type === 'opening' && !isInHumanHandoverApp
    ) {
      const currentConversationId = currentConversation.id;
      const prevConversationId = prevConversationIdRef.current;
      
      // Only clear messages if we're switching to a different conversation
      if (prevConversationId !== currentConversationId) {
        const initialMessages = config?.initial_messages || customizationData?.initial_messages || [];
        console.log('setting messages', [...initialMessages]);
        setMessages([...initialMessages]);
      }
      console.log('Loading conversation messages 2' );
      loadConversationMessages(currentConversation.id);
      prevConversationIdRef.current = currentConversationId;
    } else if (
      currentConversation.id !== null &&
      currentConversation.type === 'new'
    ) {
      // Don't load messages for 'new' conversations - they will be handled by initial messages flow
      // Keep the type as 'new' to prevent iframe restoration
    }
  }, [currentConversation]);

  useEffect(() => {
    // Early return if not in human handover app
    if (!isInHumanHandoverApp) {
      return;
    }
    
    
    if (
      liveAgentConversation &&
      customizationData &&
      (config?.initial_messages || customizationData?.initial_messages) &&
      !hasLoadedMessages.current
    ) {
      hasLoadedMessages.current = true;

      // Set loading state to true before starting
      setMessagesLoading(true);

      // Then load the conversation messages
      if (liveAgentConversation?.id) {
        // If we have an active connection to a different conversation, disconnect it first
        if (activeConnectionIdRef.current && activeConnectionIdRef.current !== liveAgentConversation.id) {
          disconnectWebSocket();
          activeConnectionIdRef.current = null;
        }
        
        loadConversationMessages(liveAgentConversation.id, true);

        // Reset file upload settings when conversation is opened
        // The actual settings will be fetched when agent joins via humanHandoverTakeConversation
        if (isInHumanHandoverApp) {
          setExplicitlyEnabledFileUpload(false);
          setFileUploadSetting(null);
        }

        // Only connect if the conversation is not resolved
        if (liveAgentConversation?.state !== 'resolved') {
          connectWebSocket(liveAgentConversation.id);
          setIsLiveAgentResolved(false);
        } else {
          setIsLiveAgentResolved(true);
          // Make sure WebSocket is disconnected for resolved conversations
          disconnectWebSocket();
          activeConnectionIdRef.current = null;
        }
      }
    }
  }, [liveAgentConversation, customizationData, isInHumanHandoverApp]);

  useEffect(() => {
    setCustomizationData(config);
    setDynamicButtons((prev) => ({
      ...prev,
      show_calendly_url:
        config?.show_calendly_at_the_start &&
        config?.calendly_integration_enabled,
      show_data_form:
        config?.show_data_collection_on_conversation_begin &&
        config?.data_collection,
      show_live_agent:
        config?.talk_to_live_agent && (config?.show_live_agent_on_conversation_begin && isAgentAvailable),
    }));
    // Update lead gen mode based on config and stored status (dynamic based on config)
    if (config?.kb_id) {
      const storedStatus = getStoredLeadGenStatus(config.kb_id, config);
      setLeadGenCompleted(storedStatus.completed);
      setLeadGenDeclined(storedStatus.declined);
      leadGenCompletedRef.current = storedStatus.completed;

      // Only enable lead gen mode if not completed/declined and config allows it
      setIsLeadGenMode(
        config?.data_collection && !storedStatus.completed && !storedStatus.declined
          ? config?.show_data_collection_on_conversation_begin
          : false
      );
    } else {
      setIsLeadGenMode(
        config?.data_collection
          ? config?.show_data_collection_on_conversation_begin
          : false
      );
    }
    setHasMic(config?.has_mic);
    
    if (config?.notification) {
      setNotification(config?.notification);
    }
  }, [config, isAgentAvailable]);

  useEffect(() => {

    // Don't overwrite messages if we're in the middle of a conversation or loading
    // BUT allow updates in preview mode for real-time preview of initial messages
    if ((currentConversation?.type === 'opening' || messages.length > 0) && !isPreviewMode) {
      return;
    }
    if (isInHumanHandoverApp) {
      const newMessages = [...(config?.initial_messages || []), ...(config?.messages || []), ...messages];
      setMessages(newMessages);
    } else {
      const newMessages = [...(config?.initial_messages || []), ...(config?.messages || [])];
      setMessages(newMessages);
    }
  }, [config?.initial_messages, config?.messages, currentConversation?.type, isInHumanHandoverApp, messages.length, isPreviewMode]);

  // Keep track of the last processed conversation ID to prevent duplicate processing
  const lastProcessedConversationRef = useRef(null);

  useEffect(() => {
    if (liveAgentConversation) {
      // Skip if we've already processed this exact conversation update
      if (lastProcessedConversationRef.current === liveAgentConversation.id) {
        return;
      }
      
      // Update the last processed conversation ID
      lastProcessedConversationRef.current = liveAgentConversation.id;
      
      if (
        liveAgentConversation?.state !== 'waiting' &&
        liveAgentConversation?.state !== 'ai' &&
        liveAgentConversation?.state !== 'resolved' &&
        activeConnectionIdRef.current !== liveAgentConversation.id
      ) {
        pollAgentTakeover(liveAgentConversation.id);
      } else {
        if (liveAgentConversation?.state === 'resolved') {
          disconnectWebSocket();
          activeConnectionIdRef.current = null;
        }
      }
    }
  }, [liveAgentConversation, isWebSocketConnected]);

  useEffect(() => {
    if (promptToSend) {
      const currentPrompt = promptToSend;
      setPromptToSend(null);
      
      setTimeout(() => {
        markAsShown();
        handleSendQuestion(currentPrompt)
          .then(() => {
            onPromptProcessed();
          })
          .catch((error) => {
            console.error('Error sending prompt:', error);
          });
      }, 100);
    }
  }, [promptToSend, onPromptProcessed, markAsShown]);

  useEffect(() => {
    setIsLiveAgentResolved(isLiveAgentConversationResolved);
  }, [isLiveAgentConversationResolved]);

  useEffect(() => {
    return () => {
      cancelAllPendingResolutions();
      // Clear resolved messages when conversation changes
      resolvedMessagesRef.current = {};
    };
  }, [currentConversation?.id, cancelAllPendingResolutions]);



  const humanHandoverTakeConversation = async () => {
    try {
      if (!liveAgentConversation?.id) {
        console.error('Cannot take conversation: Missing conversation ID');
        return;
      }

      const response = await humanHandoverService.takeConversation(
        liveAgentConversation.id
      );

      if (response.status === 200) {
        addOpenedConversation({
          ...response.data,
          open: true,
        });

        // Fetch file upload settings for this conversation when agent joins
        // This is the correct time to fetch these settings - after the Join button is clicked
        try {
          const fileSettingsResponse = await humanHandoverService.getChatFileSettingsByConversationId(
            liveAgentConversation.id
          );

          // Handle different response structures
          let isEnabled = false;
          if (typeof fileSettingsResponse.data === 'boolean') {
            isEnabled = fileSettingsResponse.data;
          } else if (fileSettingsResponse.data?.allow_file_upload !== undefined) {
            isEnabled = fileSettingsResponse.data.allow_file_upload;
          } else {
            isEnabled = false;
          }

          setFileUploadSetting(isEnabled);
          if (isEnabled) {
            setExplicitlyEnabledFileUpload(true);
          }
        } catch (fileSettingsError) {
          console.error('Error fetching file upload settings:', fileSettingsError);
          // Set to false to show the enable button
          setFileUploadSetting(false);
          setExplicitlyEnabledFileUpload(false);
        }

        // Connect to the WebSocket if not already connected
        // Use pollAgentTakeover to handle the WebSocket connection
        // This will use the existing WebSocket hook with all its handlers
        await pollAgentTakeover(liveAgentConversation.id);

        if (isInHumanHandoverApp) {
          setTimeout(() => {
            setLiveAgentConversationEvent(liveAgentConversation.id, {
              type: 'human_handover_taken',
              conversation_id: liveAgentConversation.id,
            });
          }, 100);
        }
      }
    } catch (error) {
      console.error('Error taking conversation:', error);
      setNotification({
        show: true,
        type: 'error',
        message: 'Failed to take conversation. Please try again.',
      });
    }
  };

  const humanHandoverMarkResolved = async () => {
    try {
      if (!liveAgentConversation?.id) {
        console.error('Cannot resolve conversation: Missing conversation ID');
        return;
      }

      // Check if the current user is assigned to this conversation
      if (liveAgentConversation?.user_id !== userData?.id) {
        console.error('Cannot resolve conversation: User is not assigned to this conversation');
        setNotification({
          show: true,
          type: 'error',
          message: 'You can only mark conversations as resolved if they are assigned to you.',
        });
        return;
      }

      const response = await humanHandoverService.markResolved(
        liveAgentConversation.id
      );

      if (response.status === 200) {
        setIsLiveAgentResolved(true);
        setPollResponse(false);
        setIsHumanHandover && setIsHumanHandover(false);
        addOpenedConversation({
          ...response.data,
          open: false,
        });

        // Send the human_handover_resolved message - only if NOT in the human handover app
        if (isInHumanHandoverApp) {
          sendWebSocketControlMessage('human_handover_resolved', liveAgentConversation.id);
        }

        // Disconnect the WebSocket
        disconnectWebSocket();
      }
    } catch (error) {
      console.error('Error marking conversation as resolved:', error);
      setNotification({
        show: true,
        type: 'error',
        message: 'Failed to mark conversation as resolved. Please try again.',
      });
    }
  };

  // Function to handle showing the voice preview
  const handleShowVoice = async () => {
    // If there's no current conversation, create a new one
    if (!currentConversation?.id ) {
      try {
        const newConversation = await createNewConversation();
        if (!newConversation) {
          console.error('Failed to create a new conversation for voice preview');
          // We could show an error message to the user here
          return;
        }
        // Wait a moment for the conversation to be properly set up
        await new Promise(resolve => setTimeout(resolve, 100));
      } catch (error) {
        console.error('Error creating conversation for voice preview:', error);
        return;
      }
    }

    // Show the voice preview
    setShowVoicePreview(true);
  };

  // Function to handle closing the voice preview
  const handleCloseVoice = () => {
    setShowVoicePreview(false);

    // Add a delay to allow AI Voice messages to be processed in the backend
    // before refreshing the chat history
    if (currentConversation?.id) {
      // Set loading state immediately to show loader during delay
      setMessagesLoading(true);

      setTimeout(() => {
        loadConversationMessages(currentConversation.id);
      }, 1000);
    }
  };

  useEffect(() => {
    // Cleanup function to close WebSocket connection when component unmounts
    return () => {
      // Disconnect the WebSocket
      disconnectWebSocket();
      activeConnectionIdRef.current = null;
    };
  }, [disconnectWebSocket]);



  useEffect(() => {
    if (isInHumanHandoverApp && isWebSocketConnected) {
      const liveAgentConversationEvents = getLiveAgentConversationEvents(liveAgentConversation.id);
      if (liveAgentConversationEvents) {
        liveAgentConversationEvents.forEach(event => {
          sendWebSocketControlMessage(event.type, event.conversation_id);
          deleteLiveAgentConversationEvent(liveAgentConversation.id, event);
        });
      }
    }
  }, [isInHumanHandoverApp, isWebSocketConnected, sendEventConnected]);

  useEffect(() => {
    const unlockAudio = () => {
      unlockAudioContext();
      document.removeEventListener('click', unlockAudio);
    };
    document.addEventListener('click', unlockAudio, { once: true });
  }, []);

  // Add a ref to track if we've already processed this conversation
  // const processedConversationRef = useRef(null);
  
  // useEffect(() => {
  //   // Create a unique key for this conversation state
  //   const conversationKey = `${currentConversation?.id}-${currentConversation?.type}`;
    
  //   // Skip if we've already processed this exact conversation state
  //   if (processedConversationRef.current === conversationKey) {
  //     return;
  //   }
    
  //   if(currentConversation?.type === 'new' && currentConversation?.id) {
  //     // Update the conversation type to "opening" to load messages
  //     processedConversationRef.current = conversationKey;
  //     setCurrentConversation({
  //       ...currentConversation,
  //       type: 'opening'
  //     });
  //   } else if(currentConversation?.type === 'new' && !currentConversation?.id) {
  //     processedConversationRef.current = conversationKey;
  //   }
  // }, [currentConversation, setCurrentConversation, resetCurrentConversation]);
  
  return (
    <>

      
      {/* Voice Preview Modal */}
      {showVoicePreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000000] bg-opacity-80">
          <div className="relative w-full h-full sm:max-w-[90%] md:max-w-4xl lg:max-w-4xl sm:max-h-[85vh] md:max-h-[90vh] sm:rounded-lg overflow-hidden">
            {/* External close button for mobile - only visible on small screens */}
            {/* <button
              className="absolute top-2 right-2 sm:hidden z-10 p-2 bg-black bg-opacity-50 rounded-full text-white hover:bg-opacity-70 transition-all"
              onClick={handleCloseVoice}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button> */}
            <div className="w-full h-full">
              <VoicePreview
                chatbotId={customizationData?.kb_id}
                voiceId={!isPreviewMode ? customizationData?.ai_voice_agent_id : 'a56a7938-bd6b-44b4-8e52-2c652946d528'}
                welcomeMessage={!isPreviewMode ? customizationData?.initial_messages.map(message => message.content).join(' ') : `I'm your AI Voice Assistant, fully trained on your chatbot data. Ask me anything connected to your chatbot data, and I'll provide the answers. Let's begin! ${customizationData?.initial_messages?.map(msg => msg.content).join(' ')}`}
                personalityPrompt={!isPreviewMode ? customizationData?.personality_prompt : ''}
                authToken={isInApp ? auth?.access_token : !isPreviewMode ? urlParams.get('token') : ''}
                conversationId={currentConversation?.id}
                onClose={handleCloseVoice}
                endpointType={isInApp || isPreviewMode ? WS_ENDPOINT_TYPES.BROWSER : WS_ENDPOINT_TYPES.SHARED}
              />
            </div>
          </div>
        </div>
      )}

      <V3Chat
        messages={messages}
        onSendMessage={handleSendQuestion}
        isLoading={isAnswerLoading}
        suggestedPrompts={suggestionPrompts || []}
        suggestedPromptsConfig={{
          position: config?.suggested_prompts_position || 'underLastMessage',
          kb_id: config?.kb_id || 'default',
          showOncePerKbId: config.show_prompt_messages,
          showSuggestionPrompts: showSuggestionPrompts,
          enableDragScroll: true,
          enableHoverScroll: true
        }}
        onPromptClick={(prompt, index) => {
          // Handle prompt click by sending the prompt as a message
          handleSendQuestion(prompt);
        }}
        handleMessagesOnChat={handleMessagesOnChat}
        isPreviewMode={isPreviewMode}
        isInHumanHandoverApp={isInHumanHandoverApp}
        isLiveAgentResolved={isLiveAgentResolved}
        pollResponse={pollResponse}
        isAnswerLoading={isAnswerLoading}
        isInResolutionMode={isInResolutionMode}
        isShowingCsat={isShowingCsat}
        onUserTyping={(hasContent) => {
          console.log('📝 User typing state changed:', { hasContent });
          setUserIsTyping(hasContent);
          
          if (hasContent) {
            const hasVisibleResolutionFeedback = messages.some(
              msg => msg.type === 'resolution_feedback' && !msg.answered
            );
            
            if (hasVisibleResolutionFeedback) {
              setMessages((prev) => {
                return prev.map(msg => {
                  if (msg.type === 'resolution_feedback' && !msg.answered && !msg.promptsHidden) {
                    return { ...msg, promptsHidden: true };
                  }
                  return msg;
                });
              });
              
              if (stoppedTypingTimeoutRef.current) {
                clearTimeout(stoppedTypingTimeoutRef.current);
                stoppedTypingTimeoutRef.current = null;
              }
              
              stoppedTypingTimeoutRef.current = setTimeout(() => {
                setUserIsTyping(false);
                
                setMessages((prev) => {
                  return prev.map(msg => {
                    if (msg.type === 'resolution_feedback' && !msg.answered && msg.promptsHidden) {
                      return { ...msg, promptsHidden: false };
                    }
                    return msg;
                  });
                });
                
                checkAndShowResolvedPopup();
              }, 3000);
            } else {
              cancelPendingResolutionChecks();
            }
          } else {
          
            // Cancel any existing timeout
            if (stoppedTypingTimeoutRef.current) {
              clearTimeout(stoppedTypingTimeoutRef.current);
              stoppedTypingTimeoutRef.current = null;
            }
            
            // Show prompts again if they were hidden
            setMessages((prev) => {
              return prev.map(msg => {
                if (msg.type === 'resolution_feedback' && !msg.answered && msg.promptsHidden) {
                  return { ...msg, promptsHidden: false };
                }
                return msg;
              });
            });
            
            // Check if we should show feedback immediately when input is cleared
            checkAndShowResolvedPopup();
          }
        }}
        onResolutionYes={(messageId, labelText) => handleResolutionFeedback(messageId, true, labelText)}
        onResolutionNo={(messageId, labelText) => handleResolutionFeedback(messageId, false, labelText)}
        onResolutionDismiss={handleDismissResolution}
        onCsatFeedback={(optionNumber, label, iconId) => handleCsatFeedback(optionNumber, label, iconId)}
        config={{
          ...customizationData,
          prompt_suggestions: suggestionPrompts,
          notification,
        }}
        handleSendQuestion={handleSendQuestion}
        hiddenConversation={hiddenConversation}
        showInAppHeader={showInAppHeader}
        showMenuBtn={showMenuBtn}
        hiddenPoweredByDante={
          isDanteFaq || isInApp ? true : customizationData?.remove_watermark
        }
        hideFooter={hideFooter}
        handleOpenDanteConversations={handleOpenDanteConversations}
        handleOpenDanteFaq={handleOpenDanteFaq}
        isDanteFaq={isDanteFaq}
        showCloseBtn={showCloseBtn}
        handleCloseButton={handleCloseChat}
        isInApp={isInApp}
        handleOpenLiveAgent={handleOpenLiveAgent}
        showButtons={showButtons}
        dynamicButtons={dynamicButtons}
        setCookie={setCookie}
        handleFooterButton={handleFooterButton}
        showConsent={showConsent}
        setShowConsent={setShowConsent}
        hasMic={config?.has_mic}
        showSuggestionPrompts={showSuggestionPrompts}
        sources={sources}
        openSources={openSources}
        setOpenSources={setOpenSources}
        showSources={showSources}
        sourcesLoading={sourcesLoading}
        chatImageLoader={chatImageLoader}
        humanHandoverConfig={{
          isTaken:
            liveAgentConversation?.state !== 'waiting' &&
            liveAgentConversation?.state !== 'ai',
          isFlagged: liveAgentConversation?.is_flagged,
          isAssignedToCurrentUser: liveAgentConversation?.user_id === userData?.id
        }}
        humanHandoverTakeConversation={humanHandoverTakeConversation}
        humanHandoverMarkResolved={humanHandoverMarkResolved}
        setQuestionIsQuickResponse={setQuestionIsQuickResponse}
        interactingWithLiveAgent={interactingWithLiveAgent}
        setIsLiveAgentResolved={setIsLiveAgentResolved}
        messagesLoading={messagesLoading}
        handleShowVoice={handleShowVoice}
        isPreviousConversationHasLiveAgent={isPreviousConversationHasLiveAgent}
        showEmailTranscript={showEmailTranscript}
        setShowEmailTranscript={setShowEmailTranscript}
        currectConversationId={currentConversation?.id}
        liveAgentConversationId={(() => {
    
          return liveAgentConversation?.id;
        })()}
        setNotification={setNotification}
        isLeadGenMode={isLeadGenMode}
        onTypingStart={sendWebSocketTypingStart}
        onTypingStop={sendWebSocketTypingStop}
        isTyping={isWebSocketTyping}
        typingUser={webSocketTypingUser}
        isLoadingSuggestions={isLoadingSuggestions}
        leadsGenMode={isLeadGenMode}
        leadGenInputs={leadGenInputs}
      />
    </>
  );
});

ChatManager.propTypes = {
  config: PropTypes.shape({
    initial_messages: PropTypes.array,
    messages: PropTypes.array,
    has_mic: PropTypes.bool,
    show_calendly_at_the_start: PropTypes.bool,
    calendly_integration_enabled: PropTypes.bool,
    show_data_collection_on_conversation_begin: PropTypes.bool,
    data_collection: PropTypes.bool,
    show_live_agent_on_conversation_begin: PropTypes.bool,
    talk_to_live_agent: PropTypes.bool,
    notification: PropTypes.object,
    kb_id: PropTypes.string,
    token: PropTypes.string,
    prompt_suggestions: PropTypes.array,
    suggested_prompts_enabled: PropTypes.bool,
    data_collection_consent_text: PropTypes.string,
    remove_watermark: PropTypes.bool,
  }),
  hiddenConversation: PropTypes.bool,
  showInAppHeader: PropTypes.bool,
  showMenuBtn: PropTypes.bool,
  isInApp: PropTypes.bool,
  isDanteFaq: PropTypes.bool,
  showCloseBtn: PropTypes.bool,
  handleOpenDanteConversations: PropTypes.func,
  handleOpenDanteFaq: PropTypes.func,
  handleCloseButton: PropTypes.func,
  hiddenLlmSelector: PropTypes.bool,
  initialShouldFetchCustomization: PropTypes.bool,
  isPreviewMode: PropTypes.bool,
  cookie: PropTypes.string,
  setCookie: PropTypes.func,
  forceRefetch: PropTypes.bool,
  sources: PropTypes.array,
  openSources: PropTypes.bool,
  setOpenSources: PropTypes.func,
  promptToSend: PropTypes.string,
  onPromptProcessed: PropTypes.func,
  webSocketHook: PropTypes.object,
  onNewConversationCreated: PropTypes.func,
  isDirectLink: PropTypes.bool,
};

export default ChatManager;
